/**
 * nnInteractiveSession — Multi-series GPU segmentation session state.
 *
 * Extracted from RegionGrowTool.ts (issue #209) to separate nnInteractive
 * session management from local flood fill logic.
 *
 * All Cornerstone references are injected via initializeNnSession().
 */

import type { NnInteractiveResponse, NnInteractiveInteraction } from "../hooks/useLabelingQueries";
import { nnInteractiveSegment } from "../hooks/useLabelingQueries";
import { getDefaultStore } from "jotai";
import type { PlaneContourData } from "../atoms/segmentationAtoms";
import { nnInteractiveBlockedAtom } from "../atoms/segmentationAtoms";
import { worldToVoxelZYXCramer } from "../utils/nnInteractiveCoordUtils";
import { chaikinSmooth } from "../utils/contourUtils";
import { perfMark } from "../utils/performanceTiming";
import {
  floodFill2D,
  adaptiveMinHU,
  snapToBrightestArgmax,
  DEFAULT_FWHM_FLOOR,
} from "./regionGrowHelpers";
import { logger } from "@/utils/logger";
import { isLoadedFromMask as isLoadedFromMaskAnnotation } from "../utils/annotationFlags";
import { emit as busEmit } from "../lib/viewerBus";
import { pushUndo } from "./annotationUndo";
import {
  captureSegmentationGeometry,
  copyContourForCheckpoint,
  matchesGeometryTarget,
  type SegmentationGeometryCheckpoint,
} from "../utils/segmentationGeometryCheckpoint";

// ─── Types ──────────────────────────────────────────────────────────────────

type Pt2 = [number, number];
type Pt3 = [number, number, number];

export type PlaneKey = "axial" | "coronal" | "sagittal";

/** All plane keys — use instead of hardcoded arrays. */
export const PLANE_KEYS: readonly PlaneKey[] = ["axial", "coronal", "sagittal"] as const;

/** Sibling viewport info including series UIDs for multi-series dispatch. */
export interface SiblingViewport {
  viewport: any;
  seriesUid: string | null;
  /** Whether this series is contrast-enhanced (#167). */
  hasContrast?: boolean;
}

export interface SiblingViewports {
  axial: SiblingViewport;
  // Optional: in the "seg-2up" layout only one companion plane is mounted at a
  // time (the other is not rendered), so the absent plane is omitted entirely.
  // All consumers access companions via optional chaining / `sibs[plane]?.…`.
  coronal?: SiblingViewport;
  sagittal?: SiblingViewport;
}

/** Snapshot of one session for the undo stack. */
interface SessionSnapshot {
  interactions: NnInteractiveInteraction[];
  contourUIDs: string[];
  response: NnInteractiveResponse | null;
  sessionId: string | null;
  geometry?: SegmentationGeometryCheckpoint;
}

/** Per-series session tracking for multi-series nnInteractive dispatch. */
interface SeriesSession {
  plane: PlaneKey;
  seriesUid: string;
  sessionId: string | null;
  interactions: NnInteractiveInteraction[];
  contourUIDs: string[];
  /** UIDs of click marker annotations (Length tool + shapes). */
  clickMarkers: string[];
  lastResponse: NnInteractiveResponse | null;
  viewport: any;
  spacing: [number, number, number] | null;
  /** Per-session undo stack — each entry is a snapshot before a refinement. */
  undoStack: SessionSnapshot[];
}

// ─── Cached context type (shared with RegionGrowTool) ───────────────────────

export type CachedContext = {
  viewport: any;
  element: any;
  scalarData: any;
  imageData: any;
  cols: number;
  rows: number;
  effectiveMin: number;
  effectiveMax: number;
  maxRadiusPx: number;
  smoothingIter: number;
};

// ─── Cached context (set by RegionGrowTool, read by refineNnInteractive) ────

let _cachedContext: CachedContext | null = null;

export function setCachedContext(ctx: CachedContext | null): void {
  _cachedContext = ctx;
}

// ─── Cornerstone refs (injected via initializeNnSession) ────────────────────

let _cornerstone: any = null;
let _cornerstoneTools: any = null;
let _csUtils: any = null;

/**
 * Inject Cornerstone references so factory-closure functions can operate.
 * Must be called once from createRegionGrowToolClass().
 */
export function initializeNnSession(cs: any, csTools: any, csU: any): void {
  _cornerstone = cs;
  _cornerstoneTools = csTools;
  _csUtils = csU;
}

// ─── Module-level NN state ──────────────────────────────────────────────────

/** Timestamp of last nnInteractive API failure. When set, nnInteractive is
 *  temporarily disabled to avoid hammering a failing backend. Automatically
 *  re-enables after NN_COOLDOWN_MS so transient GPU errors (OOM, cold start)
 *  don't permanently disable the feature for the rest of the study. */
let _nnFailedAt: number | null = null;
const NN_COOLDOWN_MS = 60_000; // 60 seconds

/** Max accepted segmentation volume in mm³. Per the workflow spec (item 8 —
 *  whole-organ rejection), reject anything larger than 25% of a typical
 *  150,000 mm³ (150 mL) adult kidney. A 37,500 mm³ segmentation is well past
 *  any plausible single kidney stone (~12 mm diameter would be ~900 mm³) and
 *  cleanly catches whole-organ false positives where the GPU/region-grow
 *  swallows kidney parenchyma instead of just the stone. */
export const MAX_STONE_VOLUME_MM3 = 37500;

/** Organ-scale volume ceiling (mm³). This is the DEFAULT ceiling for every
 *  category; only stone mode (is_stone) tightens it to MAX_STONE_VOLUME_MM3.
 *  Non-stone targets (organ masks, lymph nodes, most lesions) can be large, so
 *  the stone guard would reject valid results. ~2 L is generous for a kidney/
 *  liver yet still catches a runaway leak into the whole abdomen. Selected via
 *  _nnContext.isStone at each guard site. */
export const MAX_ORGAN_VOLUME_MM3 = 2_000_000;

/** Half-size (mm) of the fallback bbox applied to the primary axial seed after
 *  an oversized first attempt. 5 mm → 10 mm cube, tight enough that small
 *  (~2–5 mm) stones near a kidney edge can't relock the model onto cortex on
 *  retry. Larger stones still trigger the wider FWHM-adaptive bbox path below
 *  when scalar data is available.
 *  TODO: replace with HU-threshold region-grow bbox (FWHM-adaptive) to tighten
 *  around the actual stone voxels — see project_stone_segmentation_literature. */
const PRIMARY_BBOX_HALF_MM = 5;

/** Consecutive oversized segmentations on the same annotation. Reset on success
 *  or when starting a new annotation. After 2 consecutive oversized results,
 *  falls back to local flood fill. */
let _consecutiveOversizedCount = 0;

export function getConsecutiveOversizedCount(): number {
  return _consecutiveOversizedCount;
}

export function resetConsecutiveOversizedCount(): void {
  _consecutiveOversizedCount = 0;
}

/**
 * Pure decision function for the volume guard, exported so tests don't have
 * to reimplement the threshold/strike logic. Production callers (the volume
 * guard branch in `handleMultiSeriesNnClick`) use this to keep the test and
 * the runtime in agreement on what counts as "oversized" and when a second
 * strike triggers fallback.
 *
 * @param volumeMm3 the segmentation volume returned by nnInteractive
 * @param priorOversizedCount the strike count BEFORE this result
 *   (so caller can pass `_consecutiveOversizedCount` directly)
 */
export type VolumeGuardOutcome = "accept" | "reject_retry" | "reject_fallback";
export function classifyVolumeGuard(
  volumeMm3: number,
  priorOversizedCount: number,
  ceilingMm3: number = MAX_ORGAN_VOLUME_MM3,
): VolumeGuardOutcome {
  if (volumeMm3 <= ceilingMm3) return "accept";
  // Oversized — this would push the strike count to priorOversizedCount + 1.
  return priorOversizedCount + 1 >= 2 ? "reject_fallback" : "reject_retry";
}

export function isNnInteractiveAvailable(): boolean {
  if (_nnFailedAt === null) return true;
  if (Date.now() - _nnFailedAt >= NN_COOLDOWN_MS) {
    _nnFailedAt = null; // cooldown expired, re-enable
    return true;
  }
  return false;
}

export function setNnFailedAt(ts: number | null): void {
  _nnFailedAt = ts;
}

let _nnRequestInFlight = false;
let _cancelPendingNnOperation: (() => void) | null = null;

// Changes whenever the current NN session ends, including case navigation.
let _nnSessionGeneration = 0;

export function getNnRequestInFlight(): boolean {
  return _nnRequestInFlight;
}

export function setNnRequestInFlight(val: boolean): void {
  _nnRequestInFlight = val;
}

/** Active sessions keyed by plane. */
const _multiSessions = new Map<PlaneKey, SeriesSession>();

export function getMultiSessions(): Map<PlaneKey, SeriesSession> {
  return _multiSessions;
}

// ─── NN-specific callbacks + setters ────────────────────────────────────────

let _setNnLoading:
  | ((loading: {
      isLoading: boolean;
      viewportId: string | null;
      companionViewportIds?: string[];
    }) => void)
  | null = null;

export function setNnInteractiveLoadingCallback(
  cb:
    | ((loading: {
        isLoading: boolean;
        viewportId: string | null;
        companionViewportIds?: string[];
      }) => void)
    | null,
): void {
  _setNnLoading = cb;
}

let _onNnLayoutSwitch: (() => void) | null = null;

export function setNnInteractiveLayoutCallback(cb: (() => void) | null): void {
  _onNnLayoutSwitch = cb;
}

/** Emit cross-viewport sync so each scrollbar's "projected slice" marker
 *  (orange tick + highlighted thumb) updates. Used by the GPU companion path
 *  and the local fallback path so both paint the same scrollbar UI. */
export function notifyNnCrossViewportSync(positions: {
  axial: number;
  coronal: number;
  sagittal: number;
}): void {
  busEmit("nnCrossViewportSync", positions);
}

/** Callback to get all hanging protocol viewports + series UIDs.
 *  Returns { axial, coronal, sagittal } with viewport and seriesUid, or null. */
let _getSiblingViewports: (() => SiblingViewports | null) | null = null;

export function setGetSiblingViewportsCallback(cb: (() => SiblingViewports | null) | null): void {
  _getSiblingViewports = cb;
}

export function getSiblingViewports(): SiblingViewports | null {
  return _getSiblingViewports?.() ?? null;
}

/** Callback to update the 3D viewer with segmentation result (null clears). */
let _onNnSegmentationResult:
  | ((
      result: {
        contours: Record<string, number[][][]>;
        autoGenerated?: Record<string, boolean[]>;
        spacing: [number, number, number];
        dimensions: [number, number, number];
        origin: [number, number, number];
        volume_mm3: number;
        max_diameter_mm: number;
        rowCosines: [number, number, number];
        columnCosines: [number, number, number];
        sliceDirection: [number, number, number];
        planeContours?: {
          coronal?: PlaneContourData;
          sagittal?: PlaneContourData;
        };
      } | null,
    ) => void)
  | null = null;

export function setNnSegmentationResultCallback(cb: typeof _onNnSegmentationResult): void {
  _onNnSegmentationResult = cb;
}

/** Context needed to call nnInteractive API (set from React).
 *  seriesUid removed — resolved at click time from _getSiblingViewports(). */
let _nnContext: {
  projectId: string;
  studyUid: string;
  /** Stone-only mode: use the stone-focusing seed bbox and the tight stone
   *  volume ceiling. Absent = relaxed (organ-scale ceiling, no seed bbox). */
  isStone?: boolean;
} | null = null;

export function setNnInteractiveContext(
  ctx: { projectId: string; studyUid: string; isStone?: boolean } | null,
): void {
  // Drop leftover session state when the study changes (or context clears).
  // _multiSessions holds viewport refs whose underlying volumes get evicted
  // from cornerstone's cache on case navigation — touching them throws inside
  // getImageIds/getImageData. Reusing them across a save on the same case is
  // also wrong: the stale `lastResponse`/`contourUIDs` cause the "second
  // stone lumps with first" segment-index collision.
  const studyChanged = !ctx || _nnContext?.studyUid !== ctx.studyUid;
  if (studyChanged) {
    endNnSession();
  }
  _nnContext = ctx;
  // Reset availability when context changes (new study) so we retry nnInteractive
  _nnFailedAt = null;
  // Reset the oversized-strike counter too: a strike from an unsaved oversized
  // attempt in one mode (e.g. organ, 2 L ceiling) must not carry into the next
  // (e.g. lesion, 37.5k ceiling) when the active category changes mid-study.
  _consecutiveOversizedCount = 0;
}

/**
 * Drop all in-memory session state without removing cornerstone annotations.
 *
 * Used after save (the contour polylines are committed and locked on canvas
 * by the panel, so the session's bookkeeping is no longer load-bearing) and
 * on case navigation (stale viewport refs would crash on getImageData once
 * the previous study's volumes are evicted).
 *
 * Distinct from `rejectNnInteractive`, which also removes the contour
 * annotations from cornerstone — that's the wrong move after save because
 * the user expects the saved mask to keep rendering.
 */
export function endNnSession(): void {
  // Invalidate outstanding work before clearing session bookkeeping.
  _nnSessionGeneration += 1;
  _cancelPendingNnOperation = null;
  _multiSessions.clear();
  _cachedContext = null;
  _nnRequestInFlight = false;
  _consecutiveOversizedCount = 0;
  syncSessionActive();
}

/**
 * Drop ONE plane's in-memory session WITHOUT removing its committed contour
 * annotations from canvas. Used when a companion plane's viewport unmounts —
 * e.g. switching the seg-2up companion coronal⇄sagittal removes the previous
 * plane's viewport. Its stale ref would otherwise crash later session iteration
 * inside getImageData/getImageIds. Same "keep the contours, drop the
 * bookkeeping" contract as endNnSession, scoped to a single plane.
 */
export function dropSessionForPlane(plane: PlaneKey): void {
  if (_multiSessions.delete(plane)) {
    syncSessionActive();
  }
}

export function getNnContext(): typeof _nnContext {
  return _nnContext;
}

let _showNnToast: ((message: string) => void) | null = null;

export function setNnToastCallback(cb: ((message: string) => void) | null): void {
  _showNnToast = cb;
}

export function showNnToast(message: string): void {
  _showNnToast?.(message);
}

type ClickMarker = {
  worldPoint: [number, number, number];
  label: "positive" | "negative";
  viewportId: string;
  imageId: string;
};
let _setNnClickMarkers: ((updater: (prev: ClickMarker[]) => ClickMarker[]) => void) | null = null;

export function setNnClickMarkersCallback(cb: typeof _setNnClickMarkers): void {
  _setNnClickMarkers = cb;
}

let _setNnSessionActive: ((active: boolean) => void) | null = null;

export function setNnSessionActiveCallback(cb: ((active: boolean) => void) | null): void {
  _setNnSessionActive = cb;
}

/** Notify React that session active state changed.
 *  Drives the refinement cursor (Shift→green, Ctrl→red), so it must only
 *  return true when refinement is actually possible. Fallback-only sessions
 *  exist purely so the 3D viewer + scrollbar markers can render local
 *  contours; they do NOT support refinement (the GPU is unreachable), so
 *  they shouldn't trigger the refinement cursor. */
function syncSessionActive(): void {
  _setNnSessionActive?.(_multiSessions.size > 0 && !isFallbackOnly());
}

let _handleMultiSeriesNnClick:
  | ((
      worldPoint: [number, number, number],
      clickedPlane: PlaneKey,
      label: "positive" | "negative",
      ctx: any,
      isRefinement?: boolean,
    ) => Promise<boolean>)
  | null = null;

export function setHandleMultiSeriesNnClick(fn: typeof _handleMultiSeriesNnClick): void {
  _handleMultiSeriesNnClick = fn;
}

// ─── Resample target (shared constant) ─────────────────────────────────────

const RESAMPLE_TARGET = 200;

/**
 * Chaikin-smooth every polyline in an nnInteractive contour map in place.
 * The model returns mask-traced pixel coordinates with sharp 90° staircase
 * corners; we apply this once at receipt so both the 2D Cornerstone
 * annotations and the 3D viewer (which reads session.lastResponse.contours
 * directly) render rounded contours from the first click — matching the
 * post-bump-edit appearance.
 */
const NN_CONTOUR_SMOOTH_ITER = 2;
function smoothNnContourMap(contours: Record<string, number[][][]> | undefined): void {
  if (!contours) return;
  for (const sliceKey of Object.keys(contours)) {
    const polys = contours[sliceKey];
    for (let i = 0; i < polys.length; i++) {
      const p = polys[i];
      if (p.length >= 4) polys[i] = chaikinSmooth(p as Pt2[], NN_CONTOUR_SMOOTH_ITER);
    }
  }
}

/** Resample a closed contour to a fixed number of evenly-spaced points. */
function resampleContour(points: Pt2[], targetCount: number = RESAMPLE_TARGET): Pt2[] {
  const n = points.length;
  if (n <= 3 || n <= targetCount) return points;

  const cumLen: number[] = [0];
  for (let i = 1; i <= n; i++) {
    const [r1, c1] = points[i - 1];
    const [r2, c2] = points[i % n];
    cumLen.push(cumLen[i - 1] + Math.sqrt((r2 - r1) ** 2 + (c2 - c1) ** 2));
  }
  const totalLen = cumLen[n];
  if (totalLen === 0) return points;

  const step = totalLen / targetCount;
  const resampled: Pt2[] = [];
  let segIdx = 0;

  for (let i = 0; i < targetCount; i++) {
    const targetDist = i * step;
    while (segIdx < n - 1 && cumLen[segIdx + 1] < targetDist) {
      segIdx++;
    }
    const segStart = cumLen[segIdx];
    const segEnd = cumLen[segIdx + 1];
    const segLen = segEnd - segStart;
    const t = segLen > 0 ? (targetDist - segStart) / segLen : 0;
    const [r1, c1] = points[segIdx];
    const [r2, c2] = points[(segIdx + 1) % n];
    resampled.push([r1 + t * (r2 - r1), c1 + t * (c2 - c1)]);
  }

  let topIdx = 0;
  let topRow = resampled[0][0];
  for (let i = 1; i < resampled.length; i++) {
    if (resampled[i][0] < topRow) {
      topRow = resampled[i][0];
      topIdx = i;
    }
  }
  if (topIdx > 0) {
    return resampled.slice(topIdx).concat(resampled.slice(0, topIdx));
  }
  return resampled;
}

// ─── nnInteractive coordinate helpers ──────────────────────────────────────

/**
 * Convert a world-space point to native voxel [z, y, x] for any series.
 */
export function worldToNativeVoxelZYX(
  worldPoint: Pt3,
  viewport: any,
  cornerstoneCore: any,
): [number, number, number] | null {
  const imageIds: string[] = viewport.getImageIds?.() ?? [];
  if (imageIds.length < 2) return null;

  try {
    const ipm0 = cornerstoneCore.metaData.get("imagePlaneModule", imageIds[0]);
    if (!ipm0?.imagePositionPatient || !ipm0?.rowCosines || !ipm0?.columnCosines) return null;

    const origin = ipm0.imagePositionPatient as [number, number, number];
    const rowCos = ipm0.rowCosines as [number, number, number];
    const colCos = ipm0.columnCosines as [number, number, number];

    const imgData = viewport.getImageData?.();
    const colSp = imgData?.spacing?.[0] ?? ipm0.columnPixelSpacing ?? 1;
    const rowSp = imgData?.spacing?.[1] ?? ipm0.rowPixelSpacing ?? 1;

    const ipm1 = cornerstoneCore.metaData.get("imagePlaneModule", imageIds[1]);
    if (!ipm1?.imagePositionPatient) return null;
    const ipp1 = ipm1.imagePositionPatient as [number, number, number];
    const sliceVec: Pt3 = [ipp1[0] - origin[0], ipp1[1] - origin[1], ipp1[2] - origin[2]];
    const sliceSp = Math.sqrt(sliceVec[0] ** 2 + sliceVec[1] ** 2 + sliceVec[2] ** 2);
    if (sliceSp < 0.01) return null;
    const sliceDir: Pt3 = [sliceVec[0] / sliceSp, sliceVec[1] / sliceSp, sliceVec[2] / sliceSp];

    logger.debug(
      `[worldToNativeVoxelZYX] origin=[${origin.map((v) => v.toFixed(1))}] rowCos=[${rowCos}] colCos=[${colCos}] sliceDir=[${sliceDir.map((v) => v.toFixed(3))}] colSp=${colSp} rowSp=${rowSp} sliceSp=${sliceSp.toFixed(3)} nSlices=${imageIds.length}`,
    );

    return worldToVoxelZYXCramer(
      worldPoint,
      origin,
      rowCos,
      colCos,
      sliceDir,
      colSp,
      rowSp,
      sliceSp,
    );
  } catch {
    return null;
  }
}

/**
 * Project pixel-space contour points to world coordinates using the
 * imagePlaneModule for a specific imageId — i.e. that slice's own
 * imagePositionPatient / row & column cosines / pixel spacing.
 *
 * The contour pair is interpreted as `[row, col]` in image pixel space.
 * For axial: row=patient-Y, col=patient-X.
 * For coronal: row=patient-Z, col=patient-X (matches backend's `[z, x]`).
 * For sagittal: row=patient-Z, col=patient-Y (matches backend's `[z, y]`).
 * In every case the standard image→world DICOM projection works:
 *   world = ipp + col * colSp * rowCos + row * rowSp * colCos.
 */
function nnContourToWorldPointsFromIPM(contourPoints: number[][], imageId: string): Pt3[] | null {
  if (!_cornerstone || !contourPoints.length) return null;
  let ipm: any;
  try {
    ipm = _cornerstone.metaData.get("imagePlaneModule", imageId);
  } catch (err) {
    // Surface the failure — silently dropping a contour leaves the user
    // with a missing companion mask and no breadcrumb to debug from.
    console.warn("[nnInteractiveSession] imagePlaneModule lookup threw", { imageId, err });
    return null;
  }
  const ipp = ipm?.imagePositionPatient;
  if (!ipp) {
    console.warn("[nnInteractiveSession] Skipping contour: no imagePositionPatient in IPM", {
      imageId,
      hasIpm: !!ipm,
    });
    return null;
  }
  const rowCos = ipm.rowCosines ?? [1, 0, 0];
  const colCos = ipm.columnCosines ?? [0, 1, 0];
  const colSp = ipm.columnPixelSpacing ?? 1;
  const rowSp = ipm.rowPixelSpacing ?? 1;
  return contourPoints.map(
    ([row, col]) =>
      [
        ipp[0] + col * colSp * rowCos[0] + row * rowSp * colCos[0],
        ipp[1] + col * colSp * rowCos[1] + row * rowSp * colCos[1],
        ipp[2] + col * colSp * rowCos[2] + row * rowSp * colCos[2],
      ] as Pt3,
  );
}

/**
 * Extract [z, y, x] spacing in mm from viewport image data.
 */
function getSpacingZYX(viewport: any, cornerstoneCore?: any): [number, number, number] | null {
  const imageData = viewport.getImageData?.();
  if (!imageData?.spacing) return null;
  const [colSp, rowSp, sliceSp] = imageData.spacing;

  let zSpacing = sliceSp;
  if (zSpacing < 0.1 && cornerstoneCore) {
    const currentImageId = viewport.getCurrentImageId?.();
    if (currentImageId) {
      try {
        const ipm = cornerstoneCore.metaData.get("imagePlaneModule", currentImageId);
        if (ipm?.sliceThickness && ipm.sliceThickness > 0) {
          zSpacing = ipm.sliceThickness;
        }
      } catch {
        /* noop */
      }
    }
    if (zSpacing < 0.1) {
      const allImageIds: string[] = viewport.getImageIds?.() ?? [];
      if (allImageIds.length >= 2) {
        try {
          const ipm0 = cornerstoneCore.metaData.get("imagePlaneModule", allImageIds[0]);
          const ipm1 = cornerstoneCore.metaData.get("imagePlaneModule", allImageIds[1]);
          if (ipm0?.imagePositionPatient && ipm1?.imagePositionPatient) {
            const dx = ipm1.imagePositionPatient[0] - ipm0.imagePositionPatient[0];
            const dy = ipm1.imagePositionPatient[1] - ipm0.imagePositionPatient[1];
            const dz = ipm1.imagePositionPatient[2] - ipm0.imagePositionPatient[2];
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (dist > 0.1) zSpacing = dist;
          }
        } catch {
          /* noop */
        }
      }
    }
    if (zSpacing < 0.1) zSpacing = 5.0;
  }

  return [zSpacing, rowSp, colSp];
}

// ─── Exported: refineNnInteractive ──────────────────────────────────────────

/**
 * Refine the current nnInteractive session from any tool (zoom, pan, bump, etc).
 * Called by the global mousedown interceptor in useViewerHotkeys.
 *
 * Substitutes the clicked plane's viewport into the cached context so the
 * spinner overlay + click marker render on the viewport the user actually
 * clicked (not the axial viewport, which is what `_cachedContext` holds
 * from the original primary click).
 *
 * Returns false while the reader study blocks nnInteractive (ENG-188), so the
 * click falls through to the active Cornerstone tool.
 */
export function refineNnInteractive(
  worldPoint: [number, number, number],
  clickedPlane: PlaneKey,
  label: "positive" | "negative",
): boolean {
  if (getDefaultStore().get(nnInteractiveBlockedAtom)) return false;
  if (!_handleMultiSeriesNnClick || !_cachedContext || _nnRequestInFlight) return false;
  const sib = _getSiblingViewports?.()?.[clickedPlane];
  const ctx: CachedContext = sib?.viewport
    ? { ..._cachedContext, viewport: sib.viewport, element: sib.viewport.element }
    : _cachedContext;
  _handleMultiSeriesNnClick(worldPoint, clickedPlane, label, ctx, true);
  return true;
}

// ─── Exported: hasNnInteractiveSession ──────────────────────────────────────

/** Whether an nnInteractive session is active (for hotkey routing). */
export function hasNnInteractiveSession(): boolean {
  return _multiSessions.size > 0;
}

/** Whether every active session is a fallback session (no GPU available).
 *  When true, refinement (Shift/Ctrl+Click) should be disabled — the GPU is
 *  unreachable so refinement clicks would just retry the failed call. */
export function isFallbackOnly(): boolean {
  if (_multiSessions.size === 0) return false;
  let allFallback = true;
  _multiSessions.forEach((session) => {
    if (!session.lastResponse?.isFallback) allFallback = false;
  });
  return allFallback;
}

// ─── Factory closure functions (now module-level, use injected refs) ────────

/** Build the ctx object needed by createAnnotationsFromNnResponse for any viewport. */
function buildCtxForViewport(viewport: any, cachedContext: CachedContext | null): CachedContext {
  const imageData = viewport.getImageData?.();
  // On VolumeViewport with progressive HTJ2K loading the `scalarData` getter
  // calls into VoxelManager and throws "No scalar data available" when the
  // volume isn't held as one flat typed array. See RegionGrowTool.ts:402 for
  // the same trap. Treat a throwing getter as "no scalars" — the FWHM bbox
  // path at the only consumer (line ~2411) already degrades to a fixed-cube
  // bbox when scalarData is missing, so the click still reaches the backend.
  let scalarData: any = undefined;
  try {
    scalarData = imageData?.scalarData;
  } catch (err) {
    console.warn("[nnInteractive] scalarData unavailable on viewport, FWHM bbox skipped", err);
  }
  return {
    viewport,
    element: viewport.element,
    scalarData,
    imageData,
    cols: imageData?.dimensions?.[0] ?? 0,
    rows: imageData?.dimensions?.[1] ?? 0,
    effectiveMin: cachedContext?.effectiveMin ?? 0,
    effectiveMax: cachedContext?.effectiveMax ?? 3000,
    maxRadiusPx: cachedContext?.maxRadiusPx ?? 50,
    smoothingIter: cachedContext?.smoothingIter ?? 2,
  };
}

/**
 * Build Cornerstone annotations for all slices in an nnInteractive response.
 * Returns the UIDs of the created annotations.
 */
export function createAnnotationsFromNnResponse(
  response: NnInteractiveResponse,
  ctx: CachedContext,
  segmentIndex: number,
  plane: PlaneKey = "axial",
  strict = false,
): string[] {
  const vtkImageData = ctx.imageData?.imageData;
  if (!vtkImageData || !_cornerstoneTools || !_csUtils) {
    if (strict) throw new Error("NN viewport is not ready for contour replacement");
    return [];
  }

  const allImageIds: string[] = ctx.viewport.getImageIds?.() ?? [];
  const activeSeg = _cornerstoneTools.segmentation.getActiveSegmentation?.(ctx.viewport.id);
  const segId =
    activeSeg?.segmentationId ??
    _cornerstoneTools.segmentation.state.getSegmentations()?.[0]?.segmentationId ??
    "unknown";
  const camera = ctx.viewport.getCamera?.() ?? {};
  const viewPlaneNormal = camera.viewPlaneNormal ?? [0, 0, 1];
  const viewUp = camera.viewUp ?? [0, -1, 0];
  const forUID = ctx.viewport.getFrameOfReferenceUID?.();

  // Clear THIS plane's stale contours for this segment before redrawing, so a
  // re-segment can't stack a doubled set on top of contours orphaned by a
  // dropped companion session (e.g. after a coronal⇄sagittal toggle). Scoped to
  // this plane's normal so the other planes' contours — same segmentation,
  // segment and FrameOfReference — are left intact.
  pruneContoursForPlane(
    _cornerstoneTools,
    ctx.element,
    segId,
    segmentIndex,
    viewPlaneNormal,
    strict,
  );

  // Pick the contour map appropriate for this viewport's plane. The backend
  // ships axial-indexed `contours` plus optional `coronal_contours` (keyed by
  // y, points [z, x]) and `sagittal_contours` (keyed by x, points [z, y]).
  // Companion calls land on coronal/sagittal viewports and must consume their
  // own plane's map — feeding axial-indexed data to a non-axial viewport
  // collapses every slice to a single plane in the 3D viewer because the
  // slice-axis interpretation doesn't match.
  const contourMap =
    plane === "coronal" && response.coronal_contours
      ? response.coronal_contours
      : plane === "sagittal" && response.sagittal_contours
        ? response.sagittal_contours
        : response.contours;

  const uids: string[] = [];

  for (const [sliceIdxStr, contours] of Object.entries(contourMap)) {
    const sliceIndex = parseInt(sliceIdxStr, 10);
    if (isNaN(sliceIndex) || sliceIndex < 0 || sliceIndex >= allImageIds.length) continue;

    const imageId = allImageIds[sliceIndex];

    for (const contourPts of contours) {
      if (!contourPts || contourPts.length < 3) continue;

      const asPixels: Pt2[] = contourPts as Pt2[];

      // Contours are pre-smoothed at receipt via smoothNnContourMap; here we
      // only need to ensure enough points for resampleContour's even spacing.
      let resampled: Pt2[];
      if (asPixels.length <= RESAMPLE_TARGET) {
        let upsampled = asPixels;
        while (upsampled.length <= RESAMPLE_TARGET) {
          upsampled = chaikinSmooth(upsampled, 1);
        }
        resampled = resampleContour(upsampled);
      } else {
        resampled = resampleContour(asPixels);
      }

      // Project pixel-space contour points to world via the slice's own
      // imagePlaneModule. This works correctly regardless of viewport type
      // (stack vs volume) — vtkImageData.indexToWorld on a stack viewport
      // ignores the slice axis and would collapse all slices to the
      // current-slice's world position.
      const worldPoints = nnContourToWorldPointsFromIPM(resampled, imageId);
      if (!worldPoints || worldPoints.length === 0) continue;

      const uid =
        _csUtils.uuidv4?.() ?? `nn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const annotation = {
        annotationUID: uid,
        metadata: {
          toolName: "PlanarFreehandContourSegmentationTool",
          referencedImageId: imageId,
          FrameOfReferenceUID: forUID,
          viewPlaneNormal: [...viewPlaneNormal],
          viewUp: [...viewUp],
          sliceIndex,
        },
        data: {
          contour: { polyline: worldPoints, closed: true },
          segmentation: { segmentationId: segId, segmentIndex },
          handles: {
            points: [],
            activeHandleIndex: null,
            textBox: {
              hasMoved: false,
              worldPosition: [0, 0, 0],
              worldBoundingBox: {
                topLeft: [0, 0, 0],
                topRight: [0, 0, 0],
                bottomLeft: [0, 0, 0],
                bottomRight: [0, 0, 0],
              },
            },
          },
        },
        highlighted: false,
        isLocked: false,
        isVisible: true,
        invalidated: false,
        _regionGrow: true,
        autoGenerated: false,
      };

      _cornerstoneTools.annotation.state.addAnnotation(annotation, ctx.element);
      try {
        _cornerstoneTools.utilities.contourSegmentation.addContourSegmentationAnnotation(
          annotation,
        );
      } catch (err) {
        if (strict) throw err;
        console.warn(
          "[nnInteractive] addContourSegmentationAnnotation failed for slice",
          sliceIndex,
          err,
        );
      }

      uids.push(uid);
    }
  }

  // Render all viewports to show the new contours
  try {
    ctx.viewport.render?.();
    _cornerstoneTools.utilities.triggerAnnotationRender(ctx.element);
  } catch {
    /* noop */
  }

  return uids;
}

/** Get or create a SeriesSession for the given plane. */
function getOrCreateSession(
  plane: PlaneKey,
  seriesUid: string,
  viewport: any,
  spacing: [number, number, number] | null,
): SeriesSession {
  let session = _multiSessions.get(plane);
  if (!session) {
    session = {
      plane,
      seriesUid,
      sessionId: null,
      interactions: [],
      contourUIDs: [],
      clickMarkers: [],
      lastResponse: null,
      viewport,
      spacing,
      undoStack: [],
    };
    _multiSessions.set(plane, session);
    syncSessionActive();
  }
  // Always update viewport ref (it can change on re-render)
  session.viewport = viewport;
  return session;
}

/** Keep request bookkeeping private until geometry has been applied successfully. */
function snapshotSession(session: SeriesSession): SessionSnapshot {
  return {
    interactions: [...session.interactions],
    contourUIDs: [...session.contourUIDs],
    response: session.lastResponse,
    sessionId: session.sessionId,
  };
}

function restoreSessionState(session: SeriesSession, snapshot: SessionSnapshot): void {
  session.interactions = [...snapshot.interactions];
  session.contourUIDs = [...snapshot.contourUIDs];
  session.lastResponse = snapshot.response;
  session.sessionId = snapshot.sessionId;
}

/**
 * Remove ONE plane's stale contour annotations for a single segment, before a
 * re-segment redraws them.
 *
 * Why a plain element+segment clear is wrong: a study's axial / coronal /
 * sagittal contours all live in ONE contour segmentation under ONE segmentIndex
 * within ONE FrameOfReference, so `getAnnotations(element)` returns every
 * plane's contours and they're separable only by `viewPlaneNormal`. Removing by
 * element+segment alone wipes every plane → the "both masks disappeared"
 * regression. So we additionally filter to contours whose normal is parallel to
 * this plane's, and unregister each from the (shared) contour segmentation so
 * its annotationUIDsMap doesn't keep a dangling UID (which would blank renders).
 *
 * Without this, a plane whose session was dropped by dropSessionForPlane (e.g. a
 * coronal⇄sagittal toggle keeps its contours on canvas but discards the session
 * that tracked their UIDs) re-segments onto the orphaned contours, stacking a
 * second set → the doubled "2 masks on a slice" on the companion.
 *
 * Exported for unit testing; `cst` (the cornerstone-tools module) is injected.
 * Returns the number of contours removed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function pruneContoursForPlane(
  cst: {
    annotation: {
      state: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getAnnotations: (toolName: string, element: unknown) => any[];
        removeAnnotation: (uid: string) => void;
      };
    };
    utilities?: {
      contourSegmentation?: { removeContourSegmentationAnnotation?: (ann: unknown) => void };
    };
  },
  element: unknown,
  segmentationId: string,
  segmentIndex: number,
  viewPlaneNormal: number[],
  strict = false,
): number {
  if (!cst?.annotation?.state || !element) return 0;
  const anns =
    cst.annotation.state.getAnnotations("PlanarFreehandContourSegmentationTool", element) ?? [];
  const unregister = cst.utilities?.contourSegmentation?.removeContourSegmentationAnnotation;
  const target = {
    segmentationId,
    segmentIndex,
    viewPlaneNormal: [viewPlaneNormal[0], viewPlaneNormal[1], viewPlaneNormal[2]] as [
      number,
      number,
      number,
    ],
  };
  let removed = 0;
  // Copy first: removeAnnotation mutates the underlying state array.
  for (const ann of [...anns]) {
    // Capture and removal must select exactly the same geometry, including
    // normals stored in typed arrays by an annotation producer.
    if (!matchesGeometryTarget(ann, target)) continue;
    try {
      unregister?.(ann);
    } catch (e) {
      if (strict) throw e;
      // warn, not debug: a contour that fails to prune is exactly what
      // re-stacks into the doubling artifact this prune exists to prevent.
      logger.warn(
        `[pruneContoursForPlane] unregister failed for ${ann.annotationUID}; ` +
          "stale contour may re-stack:",
        e,
      );
    }
    try {
      cst.annotation.state.removeAnnotation(ann.annotationUID);
      removed++;
    } catch (e) {
      if (strict) throw e;
      logger.warn(
        `[pruneContoursForPlane] removeAnnotation failed for ${ann.annotationUID}; ` +
          "stale contour may re-stack:",
        e,
      );
    }
  }
  return removed;
}

/** Update the 3D viewer with current session state. Exported so external mutation paths can trigger a refresh. */
export function updateMultiSeries3DViewer(): void {
  if (!_onNnSegmentationResult || !_cornerstone) return;
  const axialSession = _multiSessions.get("axial");
  if (!axialSession?.lastResponse) {
    _onNnSegmentationResult(null);
    return;
  }

  const vp = axialSession.viewport;
  const imgData = vp.getImageData?.();
  const dims = imgData?.dimensions ?? [0, 0];
  const numSlices = vp.getImageIds?.()?.length ?? 1;
  const sp = axialSession.spacing ?? [5, 1, 1];
  const axialIds: string[] = vp.getImageIds?.() ?? [];
  let orig: [number, number, number] = imgData?.imageData?.getOrigin?.() ?? [0, 0, 0];
  let axialRowCos: [number, number, number] = [1, 0, 0];
  let axialColCos: [number, number, number] = [0, 1, 0];
  let axialSliceDir: [number, number, number] = [0, 0, 1];
  // Override sp[0] (slice spacing) with the actual IPP-delta length when
  // available — vtk's imageData.spacing[2] can disagree with the real
  // inter-slice distance, and that's what was throwing off the 3D viewer's
  // per-plane reconstruction (multiplying slice index by the wrong spacing
  // for ~60+ slices puts companion contours far from the axial contours).
  let axialSliceSp = sp[0];
  if (axialIds.length >= 2) {
    try {
      const ipm0 = _cornerstone.metaData.get("imagePlaneModule", axialIds[0]);
      const ipm1 = _cornerstone.metaData.get("imagePlaneModule", axialIds[1]);
      if (ipm0?.imagePositionPatient) orig = ipm0.imagePositionPatient;
      if (ipm0?.rowCosines) axialRowCos = ipm0.rowCosines;
      if (ipm0?.columnCosines) axialColCos = ipm0.columnCosines;
      if (ipm0?.imagePositionPatient && ipm1?.imagePositionPatient) {
        const d = [
          ipm1.imagePositionPatient[0] - ipm0.imagePositionPatient[0],
          ipm1.imagePositionPatient[1] - ipm0.imagePositionPatient[1],
          ipm1.imagePositionPatient[2] - ipm0.imagePositionPatient[2],
        ];
        const len = Math.sqrt(d[0] ** 2 + d[1] ** 2 + d[2] ** 2);
        if (len > 0.01) {
          axialSliceDir = [d[0] / len, d[1] / len, d[2] / len];
          axialSliceSp = len;
        }
      }
      // Pull row/col spacing from imagePlaneModule too — same rationale
      // as for companions: ipm has the canonical pixel spacing for the slice.
      if (ipm0?.rowPixelSpacing) sp[1] = ipm0.rowPixelSpacing;
      if (ipm0?.columnPixelSpacing) sp[2] = ipm0.columnPixelSpacing;
    } catch {
      /* use defaults */
    }
  }

  const planeContours: Record<
    string,
    {
      contours: Record<string, number[][][]>;
      autoGenerated?: Record<string, boolean[]>;
      spacing: [number, number, number];
      dimensions: [number, number, number];
      origin: [number, number, number];
      rowCosines: [number, number, number];
      columnCosines: [number, number, number];
      sliceDirection: [number, number, number];
    }
  > = {};
  // Iterate all three planes (including axial), not just companions: when a
  // user seeds in a non-axial plane, the 3D viewer needs that plane's
  // contours too. The earlier COMPANION_PLANES-only loop assumed axial was
  // always the primary, which no longer holds post stack→volume.
  for (const plane of PLANE_KEYS) {
    const session = _multiSessions.get(plane);
    if (session?.lastResponse?.contours && session.viewport) {
      const compVp = session.viewport;
      const compImg = compVp?.getImageData?.();
      const compDims = compImg?.dimensions ?? [0, 0];
      const compSlices = compVp?.getImageIds?.()?.length ?? 1;
      const compSp = session.spacing ?? [3, 1, 1];
      const compIds: string[] = compVp.getImageIds?.() ?? [];
      let compOrig: [number, number, number] = [0, 0, 0];
      let rowCos: [number, number, number] = [1, 0, 0];
      let colCos: [number, number, number] = [0, 1, 0];
      let sliceDir: [number, number, number] = [0, 0, 1];
      // Same fix as axial above: derive slice spacing from the IPP delta so
      // the 3D viewer's `slice index × slice spacing × slice direction`
      // reconstruction lines up with where the contour annotation actually
      // lives in world space. Falls back to compSp[0] when only one slice.
      let sliceSp = compSp[0];
      if (compIds.length >= 2) {
        try {
          const ipm0 = _cornerstone.metaData.get("imagePlaneModule", compIds[0]);
          const ipm1 = _cornerstone.metaData.get("imagePlaneModule", compIds[1]);
          if (ipm0?.imagePositionPatient) compOrig = ipm0.imagePositionPatient;
          if (ipm0?.rowCosines) rowCos = ipm0.rowCosines;
          if (ipm0?.columnCosines) colCos = ipm0.columnCosines;
          if (ipm0?.imagePositionPatient && ipm1?.imagePositionPatient) {
            const d = [
              ipm1.imagePositionPatient[0] - ipm0.imagePositionPatient[0],
              ipm1.imagePositionPatient[1] - ipm0.imagePositionPatient[1],
              ipm1.imagePositionPatient[2] - ipm0.imagePositionPatient[2],
            ];
            const len = Math.sqrt(d[0] ** 2 + d[1] ** 2 + d[2] ** 2);
            if (len > 0.01) {
              sliceDir = [d[0] / len, d[1] / len, d[2] / len];
              sliceSp = len;
            }
          }
          // Pull pixel spacing from imagePlaneModule too — same reason: vtk's
          // imageData.spacing can lag for stack viewports, and DICOM ipm has
          // the canonical row/col spacing for the slice.
          if (ipm0?.rowPixelSpacing) compSp[1] = ipm0.rowPixelSpacing;
          if (ipm0?.columnPixelSpacing) compSp[2] = ipm0.columnPixelSpacing;
        } catch {
          /* use defaults */
        }
      }
      planeContours[plane] = {
        contours: session.lastResponse.contours,
        autoGenerated: session.lastResponse.autoGenerated,
        spacing: [sliceSp, compSp[1], compSp[2]],
        dimensions: [compSlices, compDims[1] ?? compDims[0], compDims[0]],
        origin: [compOrig[0], compOrig[1], compOrig[2]],
        rowCosines: rowCos,
        columnCosines: colCos,
        sliceDirection: sliceDir,
      };
    }
  }

  _onNnSegmentationResult({
    contours: axialSession.lastResponse.contours,
    autoGenerated: axialSession.lastResponse.autoGenerated,
    spacing: [axialSliceSp, sp[1], sp[2]],
    dimensions: [numSlices, dims[1] ?? dims[0], dims[0]],
    origin: [orig[0], orig[1], orig[2]],
    volume_mm3: axialSession.lastResponse.volume_mm3,
    max_diameter_mm: axialSession.lastResponse.max_diameter_mm,
    rowCosines: axialRowCos,
    columnCosines: axialColCos,
    sliceDirection: axialSliceDir,
    planeContours: Object.keys(planeContours).length > 0 ? planeContours : undefined,
  });
}

/**
 * Rebuild a session's contour data from current Cornerstone annotations.
 * This syncs the session's lastResponse.contours with what's actually
 * rendered, so the 3D viewer reflects bump edits, deletes, etc.
 */
function rebuildContoursFromAnnotations(session: SeriesSession): void {
  if (!_cornerstoneTools || !session.viewport || !session.lastResponse) return;

  // For NATIVE-series companion viewports (separate DICOM stack per plane,
  // which is how the labeling layout is wired), `worldToIndex` returns the
  // native [col, row, slice] in that series' own voxel space — exactly the
  // format the 3D viewer expects. The `imageIdToIndex.has(refId)` filter
  // below naturally skips annotations from other stacks (e.g. reformatted
  // companions whose referencedImageId points at the axial volume), so we
  // don't need an extra plane-based guard. Without this rebuild, fallback
  // companion contours never reach the 3D viewer and bump edits on any
  // companion contour stop showing up in 3D.
  const allAnnotations: any[] = _cornerstoneTools.annotation.state.getAllAnnotations() ?? [];
  const imageIds: string[] = session.viewport.getImageIds?.() ?? [];
  if (imageIds.length === 0) return;
  const imageIdToIndex = new Map<string, number>();
  imageIds.forEach((id: string, idx: number) => imageIdToIndex.set(id, idx));

  // Keep autoGenerated annotations on every plane (axial + companions). The
  // 3D viewer renders them as dashed rings via the parallel `autoGenerated`
  // map below, so they no longer look like plane misalignment — they read
  // clearly as predicted/propagated slices.

  // Group contour polylines by slice index, with a parallel autoGenerated map
  // (slice -> per-contour boolean) so the 3D viewer can pick dashed vs solid.
  const contours: Record<string, number[][][]> = {};
  const autoGenerated: Record<string, boolean[]> = {};
  let skippedMissingMetadata = 0;
  for (const ann of allAnnotations) {
    if (ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
    // Skip previously-saved (locked) annotations — they belong to *other*
    // stones and should not appear in the 3D viewer for the currently-
    // drawn annotation. Without this guard, starting stone 2 re-pulls
    // stone 1's locked contours into the 3D mesh.
    if (isLoadedFromMaskAnnotation(ann)) continue;
    const refId = ann.metadata?.referencedImageId;
    if (!refId) continue;
    if (!imageIdToIndex.has(refId)) continue;

    const polyline = ann.data?.contour?.polyline;
    if (!polyline || polyline.length < 3) continue;

    const sliceIdx = imageIdToIndex.get(refId)!;

    // Convert world-space polyline back to voxel [row, col] pairs using the
    // referenced slice's DICOM imagePlaneModule. We can't call
    // `viewport.worldToIndex` — Cornerstone3D's StackViewport doesn't expose
    // that — and routing through vtkImageData would be wrong because the
    // viewport's vtkImageData represents the *currently displayed* slice
    // only. The imagePlaneModule has the exact ipp + cosines + spacing for
    // the slice the annotation belongs to, so the inversion is one dot
    // product per axis.
    const ipm = _cornerstone.metaData.get("imagePlaneModule", refId);
    const ipp = ipm?.imagePositionPatient;
    const rowCos = ipm?.rowCosines;
    const colCos = ipm?.columnCosines;
    const cSp = ipm?.columnPixelSpacing ?? 1;
    const rSp = ipm?.rowPixelSpacing ?? 1;
    if (!ipp || !rowCos || !colCos) {
      // Missing IPM means this annotation can't be projected back to voxel
      // space and won't appear in the 3D rebuild. Surface it so silent
      // disappearance from 3D is at least diagnosable from the console.
      skippedMissingMetadata++;
      console.warn(
        `[multiSeriesNn] rebuildContours skipped annotation ${ann.annotationUID} on ` +
          `${refId}: missing imagePlaneModule (ipp=${!!ipp}, rowCos=${!!rowCos}, colCos=${!!colCos})`,
      );
      continue;
    }
    const voxelContour: number[][] = [];
    for (const worldPt of polyline) {
      const dx = worldPt[0] - ipp[0];
      const dy = worldPt[1] - ipp[1];
      const dz = worldPt[2] - ipp[2];
      const col = (dx * rowCos[0] + dy * rowCos[1] + dz * rowCos[2]) / cSp;
      const row = (dx * colCos[0] + dy * colCos[1] + dz * colCos[2]) / rSp;
      voxelContour.push([row, col]);
    }

    if (voxelContour.length >= 3) {
      const key = String(sliceIdx);
      if (!contours[key]) contours[key] = [];
      if (!autoGenerated[key]) autoGenerated[key] = [];
      contours[key].push(voxelContour);
      autoGenerated[key].push(!!ann.autoGenerated);
    }
  }

  if (skippedMissingMetadata > 0) {
    console.warn(
      `[multiSeriesNn] rebuildContours: ${skippedMissingMetadata} annotation(s) skipped on ` +
        `session ${session.plane} due to missing IPM — 3D viewer will not show them`,
    );
  }
  session.lastResponse.contours = contours;
  session.lastResponse.autoGenerated = autoGenerated;
}

/**
 * Refresh the 3D viewer from current Cornerstone annotations.
 * Call after bump edits, annotation deletes, or any external modification.
 */
export function refreshViewerFromAnnotations(): void {
  // Skip rebuild during active nnInteractive API calls — annotations are
  // being removed and recreated, so the intermediate state is unreliable.
  if (_nnRequestInFlight) return;
  _multiSessions.forEach((session) => {
    if (session.lastResponse) {
      rebuildContoursFromAnnotations(session);
    }
  });
  updateMultiSeries3DViewer();
}

/**
 * Ensure a minimal session exists for the given plane/viewport so that
 * refreshViewerFromAnnotations() can extract contours from annotations
 * created by the HU threshold fallback (which bypasses nnInteractive).
 */
export function ensureSessionForFallback(viewport: any, plane: PlaneKey, seriesUid: string): void {
  // If a session already exists for this plane (created during the failed
  // multiSeriesNn call), still mark it as fallback. Otherwise its empty
  // `lastResponse` would leave `isFallbackOnly()` returning false, which
  // breaks the per-plane undo dispatch and the cursor gating.
  const existing = _multiSessions.get(plane);
  if (existing) {
    if (!existing.lastResponse?.isFallback) {
      existing.lastResponse = {
        session_id: "fallback",
        contours: {},
        volume_mm3: 0,
        max_diameter_mm: 0,
        voxel_count: 0,
        isFallback: true,
      };
      syncSessionActive();
    }
    return;
  }
  // Cornerstone spacing is [x, y, z] — pass through as-is to match getOrCreateSession contract
  const spacing: [number, number, number] | null = (() => {
    try {
      const imgData = viewport.getImageData?.();
      const sp = imgData?.spacing;
      if (!sp) return null;
      if (sp[0] == null || sp[1] == null || sp[2] == null) {
        throw new Error(
          `Incomplete spacing [${sp[0]}, ${sp[1]}, ${sp[2]}] — DICOM metadata missing`,
        );
      }
      return [sp[0], sp[1], sp[2]] as [number, number, number];
    } catch (err) {
      console.warn("[ensureSessionForFallback] spacing extraction failed:", err);
      return null;
    }
  })();
  const session = getOrCreateSession(plane, seriesUid, viewport, spacing);
  // Create a minimal lastResponse so rebuildContoursFromAnnotations can populate it.
  // Marked as isFallback so downstream consumers (e.g. stats display) know this
  // isn't a real nnInteractive session and should not display volume/diameter.
  session.lastResponse = {
    session_id: "fallback",
    contours: {},
    volume_mm3: 0,
    max_diameter_mm: 0,
    voxel_count: 0,
    isFallback: true,
  };
  // getOrCreateSession's syncSessionActive call already fired before we
  // set isFallback, so the cursor would briefly switch on as if refinement
  // were available. Re-sync now that the flag is set so isFallbackOnly()
  // can suppress the refinement cursor.
  syncSessionActive();
}

/**
 * Convert a world point to fractional (r, c) pixel coords on a loaded
 * cornerstone image, using the image's own DICOM metadata exclusively.
 *
 * Why this exists: `worldToNativeVoxelZYX` prefers vtk's spacing over DICOM's,
 * which is fine for axial primaries (vtk == DICOM there) but breaks on
 * coronal/sagittal reformats — cornerstone-3d resamples the StackViewport's
 * vtkImageData to display-friendly spacing, so vtk-frame coords no longer
 * align with the underlying pixelData buffer. Going straight from worldPt
 * through the loaded slice's IPP/IOP/pixelSpacing avoids that mismatch.
 *
 * DICOM convention recap:
 *  - rowCosines  = direction along rows = world delta as col index increments
 *  - colCosines  = direction along columns = world delta as row index increments
 *  - rowPixelSpacing    = physical mm between adjacent rows (i.e. row step)
 *  - columnPixelSpacing = physical mm between adjacent cols (i.e. col step)
 */
export interface DicomImagePlaneGeometry {
  ipp: [number, number, number];
  rowCos: [number, number, number];
  colCos: [number, number, number];
  rowSp: number;
  colSp: number;
}

/** Resolve DICOM image-plane geometry from cornerstone metadata + the loaded
 *  image. IPP/cosines live on the imagePlaneModule (not on the image object);
 *  pixelSpacing is mirrored on the image as a fallback. */
function getImagePlaneGeometry(imageId: string, image: any): DicomImagePlaneGeometry | null {
  if (!_cornerstone || !imageId) return null;
  const ipm = _cornerstone.metaData.get("imagePlaneModule", imageId);
  const ipp = ipm?.imagePositionPatient;
  const rowCos = ipm?.rowCosines;
  const colCos = ipm?.columnCosines;
  const rowSp = ipm?.rowPixelSpacing ?? image?.rowPixelSpacing;
  const colSp = ipm?.columnPixelSpacing ?? image?.columnPixelSpacing;
  if (!ipp || !rowCos || !colCos || !rowSp || !colSp) return null;
  return { ipp, rowCos, colCos, rowSp, colSp };
}

/** Project a world point onto the image plane defined by `geom`, returning
 *  fractional DICOM (r, c) pixel coords. Pure math — exported for tests. */
export function worldToImagePixelGeom(
  worldPt: [number, number, number],
  geom: DicomImagePlaneGeometry,
): { r: number; c: number } {
  const dx = worldPt[0] - geom.ipp[0];
  const dy = worldPt[1] - geom.ipp[1];
  const dz = worldPt[2] - geom.ipp[2];
  // r increments along colCos at rowPixelSpacing
  const r = (dx * geom.colCos[0] + dy * geom.colCos[1] + dz * geom.colCos[2]) / geom.rowSp;
  // c increments along rowCos at columnPixelSpacing
  const c = (dx * geom.rowCos[0] + dy * geom.rowCos[1] + dz * geom.rowCos[2]) / geom.colSp;
  return { r, c };
}

/** Inverse of worldToImagePixelGeom — recover world point from DICOM (r, c).
 *  Pure math — exported for tests. */
export function imagePixelToWorldGeom(
  r: number,
  c: number,
  geom: DicomImagePlaneGeometry,
): [number, number, number] {
  return [
    geom.ipp[0] + r * geom.rowSp * geom.colCos[0] + c * geom.colSp * geom.rowCos[0],
    geom.ipp[1] + r * geom.rowSp * geom.colCos[1] + c * geom.colSp * geom.rowCos[1],
    geom.ipp[2] + r * geom.rowSp * geom.colCos[2] + c * geom.colSp * geom.rowCos[2],
  ];
}

function worldToImagePixel(
  worldPt: [number, number, number],
  imageId: string,
  image: any,
): { r: number; c: number } | null {
  const g = getImagePlaneGeometry(imageId, image);
  return g ? worldToImagePixelGeom(worldPt, g) : null;
}

function imagePixelToWorld(
  r: number,
  c: number,
  imageId: string,
  image: any,
): [number, number, number] | null {
  const g = getImagePlaneGeometry(imageId, image);
  return g ? imagePixelToWorldGeom(r, c, g) : null;
}

/** Convert DICOM-frame (r, c) on slice z back to the viewport's vtk-frame
 *  [z, y, x]. Keeps the original z (slice index) since vtk's worldToIndex on
 *  a 2D StackViewport's depth-1 vtkImageData would return a z relative to
 *  the currently displayed slice, not the slice we loaded.
 *
 *  Returns null if metadata is missing.
 */
function dicomPixelToVtkVoxel(
  r: number,
  c: number,
  zSlice: number,
  imageId: string,
  image: any,
  vtkImg: any,
): [number, number, number] | null {
  const world = imagePixelToWorld(r, c, imageId, image);
  if (!world || !vtkImg) return null;
  const idx = vtkImg.worldToIndex(world);
  if (!idx) return null;
  // vtk worldToIndex returns [colIdx, rowIdx, sliceIdx] = [x, y, z], typically
  // floating-point. The API's pydantic schema rejects non-integer voxel coords
  // with a 422, so round here.
  return [zSlice, Math.round(idx[1]), Math.round(idx[0])];
}

/** Scale a vtk-px search radius into anisotropic DICOM-px radii so the search
 *  covers the same physical area regardless of resample ratio. Exported for tests. */
export function scaleRadiusToDicomFrame(
  vtkRadiusPx: number,
  vtkRowSp: number,
  dicomRowSp: number,
  dicomColSp: number,
): { radiusR: number; radiusC: number } {
  const mm = vtkRadiusPx * vtkRowSp;
  return {
    radiusR: Math.ceil(mm / dicomRowSp),
    radiusC: Math.ceil(mm / dicomColSp),
  };
}

/**
 * Snap a voxel seed to the brightest HU pixel in a small neighborhood.
 * Used by companion calls to ensure the seed lands on the stone (high HU)
 * rather than soft tissue when the world→voxel projection is imprecise.
 *
 * `worldPt` re-derives (row, col) on the loaded slice from DICOM metadata
 * instead of trusting vtk-frame voxelZYX. This is essential for reformat
 * companions: cornerstone-3d resamples the StackViewport's vtkImageData to
 * display-friendly spacing, so vtk-frame (y, x) coords don't match the
 * underlying DICOM pixelData buffer (off by the resample ratio). Falls back
 * to vtk-frame internally only when DICOM metadata is unavailable.
 */
const COMPANION_SNAP_RADIUS = 15; // pixels — larger radius to account for projection error

export async function snapCompanionSeed(
  viewport: any,
  voxelZYX: [number, number, number],
  worldPt: [number, number, number],
): Promise<[number, number, number]> {
  try {
    const imageIds: string[] = viewport.getImageIds?.() ?? [];
    const sliceIdx = Math.round(voxelZYX[0]);
    if (sliceIdx < 0 || sliceIdx >= imageIds.length) return voxelZYX;

    const seedImageId = imageIds[sliceIdx];
    const image = await _cornerstone.imageLoader.loadAndCacheImage(seedImageId);
    const pixelData = image?.getPixelData?.();
    const cols = image?.columns;
    const rows = image?.rows;
    if (!pixelData || !cols || !rows) return voxelZYX;

    const dicom = worldToImagePixel(worldPt, seedImageId, image);
    const seedRow = dicom ? Math.round(dicom.r) : Math.round(voxelZYX[1]);
    const seedCol = dicom ? Math.round(dicom.c) : Math.round(voxelZYX[2]);
    if (seedRow < 0 || seedRow >= rows || seedCol < 0 || seedCol >= cols) return voxelZYX;

    const slope = image.slope ?? 1;
    const intercept = image.intercept ?? 0;
    const toHU = (idx: number) => (pixelData[idx] as number) * slope + intercept;

    const imgData = viewport.getImageData?.();
    const ipm = _cornerstone.metaData.get("imagePlaneModule", seedImageId);
    const vtkRowSp = imgData?.spacing?.[1] ?? ipm?.rowPixelSpacing ?? image.rowPixelSpacing ?? 1;
    const dicomRowSp = ipm?.rowPixelSpacing ?? image.rowPixelSpacing ?? vtkRowSp;
    const dicomColSp =
      ipm?.columnPixelSpacing ?? image.columnPixelSpacing ?? imgData?.spacing?.[0] ?? 1;
    const { radiusR, radiusC } = dicom
      ? scaleRadiusToDicomFrame(COMPANION_SNAP_RADIUS, vtkRowSp, dicomRowSp, dicomColSp)
      : { radiusR: COMPANION_SNAP_RADIUS, radiusC: COMPANION_SNAP_RADIUS };

    let bestRow = seedRow,
      bestCol = seedCol;
    let bestHU = toHU(seedRow * cols + seedCol);

    // Anisotropic ellipse so the search covers the same physical area regardless
    // of pixel aspect ratio.
    for (let dr = -radiusR; dr <= radiusR; dr++) {
      for (let dc = -radiusC; dc <= radiusC; dc++) {
        const nr = seedRow + dr;
        const nc = seedCol + dc;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        const drNorm = dr / radiusR;
        const dcNorm = dc / radiusC;
        if (drNorm * drNorm + dcNorm * dcNorm > 1) continue;
        const hu = toHU(nr * cols + nc);
        if (hu > bestHU) {
          bestHU = hu;
          bestRow = nr;
          bestCol = nc;
        }
      }
    }

    if (bestRow !== seedRow || bestCol !== seedCol) {
      logger.debug(
        `[companionSnap] ${viewport.id}: seed [${seedRow},${seedCol}] HU=${toHU(seedRow * cols + seedCol).toFixed(0)} → ` +
          `snapped [${bestRow},${bestCol}] HU=${bestHU.toFixed(0)}${dicom ? " (DICOM frame)" : ""}`,
      );
    }

    // Convert DICOM-frame (bestRow, bestCol) back to vtk-frame for the return
    // contract. Without conversion, downstream callers that pass this voxel to
    // vtk indexToWorld would render at the wrong location.
    if (dicom) {
      const vtkVoxel = dicomPixelToVtkVoxel(
        bestRow,
        bestCol,
        voxelZYX[0],
        seedImageId,
        image,
        viewport.getImageData?.()?.imageData,
      );
      if (vtkVoxel) return vtkVoxel;
    }
    return [voxelZYX[0], bestRow, bestCol];
  } catch (err) {
    console.warn("[companionSnap] Failed, using original seed:", err);
    return voxelZYX;
  }
}

/**
 * Compute an FWHM-adaptive bbox on a companion viewport's slice. Loads the
 * slice DICOM, builds an HU-rescaled scalar buffer, and runs the same FWHM
 * region-grow used for the primary. Returns null on any failure (caller
 * should fall back to `computeCompanionBbox`'s projected primary-contour bbox).
 */
async function computeCompanionFwhmAdaptiveBbox(
  viewport: any,
  snappedVoxel: [number, number, number],
  spacing: [number, number, number] | null,
  effectiveMin: number,
  effectiveMax: number,
  maxRadiusPx: number,
  worldPt: [number, number, number],
): Promise<[[number, number], [number, number], [number, number]] | null> {
  try {
    const imageIds: string[] = viewport.getImageIds?.() ?? [];
    const sliceIdx = Math.round(snappedVoxel[0]);
    if (sliceIdx < 0 || sliceIdx >= imageIds.length) return null;

    const seedImageId = imageIds[sliceIdx];
    const image = await _cornerstone.imageLoader.loadAndCacheImage(seedImageId);
    const pixelData = image?.getPixelData?.();
    const cols = image?.columns;
    const rows = image?.rows;
    if (!pixelData || !cols || !rows) return null;

    // Convert to HU once — floodFill2D / adaptiveMinHU compare values directly
    // against HU thresholds (130 floor, etc), so raw uint16 values won't work.
    const slope = image.slope ?? 1;
    const intercept = image.intercept ?? 0;
    const huData = new Float32Array(cols * rows);
    for (let i = 0; i < huData.length; i++) {
      huData[i] = (pixelData[i] as number) * slope + intercept;
    }

    // huData is laid out as the cornerstone image's pixelData (DICOM frame).
    // The incoming snappedVoxel may be in vtk-frame; map to DICOM (r, c) so
    // the seed lands on the right pixel.
    let seedZYX = snappedVoxel;
    let seedDicomFrame = false;
    const dicom = worldToImagePixel(worldPt, seedImageId, image);
    if (dicom) {
      seedZYX = [snappedVoxel[0], dicom.r, dicom.c];
      seedDicomFrame = true;
    }

    // Run FWHM in DICOM-frame voxel coords; the bbox we get back is also in
    // DICOM-frame (r, c bounds on huData).
    const dicomBbox = computeFwhmAdaptiveBbox(
      huData,
      cols,
      rows,
      seedZYX,
      spacing,
      effectiveMin,
      effectiveMax,
      maxRadiusPx,
    );
    if (!dicomBbox) return null;

    if (!seedDicomFrame) return dicomBbox;

    // Convert DICOM-frame bbox edges back to vtk-frame so visualization and
    // the API receive coords consistent with the rest of the pipeline. Convert
    // all 4 corners (not just diagonal pair) — protects against vtk/DICOM frame
    // rotations that would axis-swap a diagonal-only conversion.
    const vtkImg = viewport.getImageData?.()?.imageData;
    const [[z1, z2], [r1, r2], [c1, c2]] = dicomBbox;
    const corners = vtkImg
      ? ([
          dicomPixelToVtkVoxel(r1, c1, z1, seedImageId, image, vtkImg),
          dicomPixelToVtkVoxel(r1, c2 - 1, z1, seedImageId, image, vtkImg),
          dicomPixelToVtkVoxel(r2 - 1, c1, z1, seedImageId, image, vtkImg),
          dicomPixelToVtkVoxel(r2 - 1, c2 - 1, z1, seedImageId, image, vtkImg),
        ] as Array<[number, number, number] | null>)
      : null;
    if (!corners || corners.some((c) => !c)) {
      console.warn("[multiSeriesNn] FWHM bbox: vtk conversion failed, abandoning companion FWHM");
      return null;
    }
    const ys = corners.map((p) => p![1]);
    const xs = corners.map((p) => p![2]);
    const compDims = viewport.getImageData?.()?.dimensions ?? [cols, rows, 1];
    const yLoI = Math.max(0, Math.floor(Math.min(...ys)));
    const yHiI = Math.min(compDims[1], Math.ceil(Math.max(...ys)) + 1);
    const xLoI = Math.max(0, Math.floor(Math.min(...xs)));
    const xHiI = Math.min(compDims[0], Math.ceil(Math.max(...xs)) + 1);
    return [
      [z1, z2],
      [yLoI, yHiI],
      [xLoI, xHiI],
    ];
  } catch (err) {
    console.warn("[multiSeriesNn] Failed to compute companion FWHM bbox:", err);
    return null;
  }
}

/**
 * Compute a 2D bounding box interaction for a companion viewport.
 * Projects the primary contour's world-space bbox into the companion's voxel space,
 * clamped to a single slice (the seed's Z), with padding.
 *
 * Returns the bbox in nnInteractive format: [[z1,z2],[y1,y2],[x1,x2]] (half-open) —
 * matches the library which iterates dims against image shape (C,Z,Y,X).
 * Returns null if computation fails.
 */
function computeCompanionBbox(
  primaryViewport: any,
  primaryContours: Record<string, number[][][]>,
  companionViewport: any,
  seedSliceZ: number,
  padding: number = 0,
): [[number, number], [number, number], [number, number]] | null {
  try {
    const primaryImgData = primaryViewport.getImageData?.();
    const vtkImg = primaryImgData?.imageData;
    const compImgData = companionViewport.getImageData?.();
    const compVtk = compImgData?.imageData;
    if (!vtkImg || !compVtk) return null;

    // Project every primary contour point directly into companion voxel space
    // and take the min/max there. Going through a world-AABB → 8-corner trick
    // (the previous approach) loses information when the input points happen
    // to align — e.g. the synthesized FWHM-rectangle on each slice has the
    // same y/x corners, which made coronal Y collapse to 1 voxel even when
    // the primary spanned multiple slices.
    let vMinX = Infinity,
      vMaxX = -Infinity;
    let vMinY = Infinity,
      vMaxY = -Infinity;
    let pointCount = 0;

    for (const [sliceStr, sliceContours] of Object.entries(primaryContours)) {
      const sliceIdx = Number(sliceStr);
      for (const contour of sliceContours) {
        for (const [row, col] of contour) {
          // indexToWorld expects [col, row, slice] = [x, y, z]
          const worldPt = vtkImg.indexToWorld([col, row, sliceIdx]);
          if (!worldPt) continue;
          // worldToIndex returns [col, row, slice] = [x, y, z]
          const idx = compVtk.worldToIndex(worldPt);
          if (!idx) continue;
          vMinX = Math.min(vMinX, idx[0]);
          vMaxX = Math.max(vMaxX, idx[0]);
          vMinY = Math.min(vMinY, idx[1]);
          vMaxY = Math.max(vMaxY, idx[1]);
          pointCount++;
        }
      }
    }

    if (pointCount === 0 || !isFinite(vMinX)) return null;

    // 3. Add padding and clamp to image bounds. Each in-plane axis is min-
    //    clamped to MIN_COMPANION_DIM voxels — primary stack-viewports report a
    //    2D vtkImageData (depth 1) so multi-slice primary contours collapse to
    //    a single world plane, leaving the cross-plane companion axis with
    //    zero spread. A 6-voxel minimum gives the model usable spatial prior.
    const MIN_COMPANION_DIM = 6;
    const compDims = compImgData?.dimensions ?? [512, 512];
    const expandToMin = (lo: number, hi: number, size: number): [number, number] => {
      const span = hi - lo;
      if (span >= MIN_COMPANION_DIM) return [lo, hi];
      const need = MIN_COMPANION_DIM - span;
      let newLo = lo - Math.floor(need / 2);
      let newHi = hi + Math.ceil(need / 2);
      if (newLo < 0) {
        newHi += -newLo;
        newLo = 0;
      }
      if (newHi > size) {
        newLo = Math.max(0, newLo - (newHi - size));
        newHi = size;
      }
      return [newLo, newHi];
    };
    let x1 = Math.max(0, Math.floor(vMinX) - padding);
    let x2 = Math.min(compDims[0], Math.ceil(vMaxX) + padding);
    let y1 = Math.max(0, Math.floor(vMinY) - padding);
    let y2 = Math.min(compDims[1], Math.ceil(vMaxY) + padding);
    [x1, x2] = expandToMin(x1, x2, compDims[0]);
    [y1, y2] = expandToMin(y1, y2, compDims[1]);

    // 4. 2D bbox on the seed's slice (z dimension = single slice, half-open)
    const z1 = seedSliceZ;
    const z2 = seedSliceZ + 1;

    logger.debug(
      `[multiSeriesNn] Companion bbox: z=[${z1},${z2}] y=[${y1},${y2}] x=[${x1},${x2}] ` +
        `(from ${pointCount} primary contour points, padding=${padding})`,
    );

    return [
      [z1, z2],
      [y1, y2],
      [x1, x2],
    ];
  } catch (err) {
    console.warn("[multiSeriesNn] Failed to compute companion bbox:", err);
    return null;
  }
}

/**
 * Pure geometry helper for the primary fallback bbox — exported so tests can
 * exercise the actual production math without a Cornerstone viewport.
 *
 * Builds a ±PRIMARY_BBOX_HALF_MM cube centered on the click, clamped to image
 * bounds, on the seed's slice. Returns nnInteractive format: [[z1,z2],[y1,y2],[x1,x2]]
 * (half-open) — matches the library which iterates dims against image shape (C,Z,Y,X).
 */
export function computePrimaryFallbackBboxFromDims(
  voxelZYX: [number, number, number],
  spacing: [number, number, number] | null,
  dims: [number, number, number],
): [[number, number], [number, number], [number, number]] | null {
  const [z, y, x] = voxelZYX;
  const [, ySp, xSp] = spacing ?? [1, 1, 1];
  if (!xSp || !ySp) return null;
  const halfX = Math.ceil(PRIMARY_BBOX_HALF_MM / xSp);
  const halfY = Math.ceil(PRIMARY_BBOX_HALF_MM / ySp);
  const x1 = Math.max(0, Math.floor(x) - halfX);
  const x2 = Math.min(dims[0], Math.floor(x) + halfX);
  const y1 = Math.max(0, Math.floor(y) - halfY);
  const y2 = Math.min(dims[1], Math.floor(y) + halfY);
  const zInt = Math.round(z);
  return [
    [zInt, zInt + 1],
    [y1, y2],
    [x1, x2],
  ];
}

/**
 * Compute a fixed 2D fallback bbox for the primary axial seed. Wraps the pure
 * helper above with viewport dimension lookup + try/catch.
 */
function computePrimaryFallbackBbox(
  viewport: any,
  voxelZYX: [number, number, number],
  spacing: [number, number, number] | null,
): [[number, number], [number, number], [number, number]] | null {
  try {
    const dims = viewport?.getImageData?.()?.dimensions ?? [512, 512, 1];
    return computePrimaryFallbackBboxFromDims(voxelZYX, spacing, dims);
  } catch (err) {
    console.warn("[multiSeriesNn] Failed to compute primary fallback bbox:", err);
    return null;
  }
}

/** Exported for tests so they can validate the constant matches their expectations. */
export { PRIMARY_BBOX_HALF_MM };

/** Padding (voxels) added around the FWHM mask bbox before sending to nnInteractive.
 *  Small margin tolerates click imprecision and partial-volume soft edges, while
 *  staying tight enough to exclude adjacent kidney parenchyma. */
const FWHM_BBOX_PAD_PX = 3;

/** Hard cap on FWHM mask bbox half-size (mm). If the mask grows past this, we
 *  treat it as a runaway flood and fall back to the fixed-cube bbox. Stones up
 *  to ~30 mm fit easily; anything larger is almost certainly a leak into
 *  kidney parenchyma or contrast. */
const FWHM_BBOX_MAX_HALF_MM = 15;

/**
 * Compute a tight bbox around the actual stone voxels via FWHM-adaptive
 * region grow. Replaces the fixed-cube `computePrimaryFallbackBbox` when
 * scalar pixel data is available — produces a bbox that just contains the
 * high-HU voxels around the click rather than a generous margin.
 *
 * Returns the bbox in nnInteractive format: [[z1,z2],[y1,y2],[x1,x2]] (half-open).
 * Returns null if the fill is empty, the seed is below the stone floor, or the
 * mask grows beyond FWHM_BBOX_MAX_HALF_MM (likely runaway). Caller should fall
 * back to computePrimaryFallbackBbox in those cases.
 *
 * Reuses pure helpers from regionGrowHelpers.ts (same logic as RegionGrowTool's
 * local flood-fill path, see project_stone_segmentation_literature).
 */
function computeFwhmAdaptiveBbox(
  scalarData: any,
  cols: number,
  rows: number,
  voxelZYX: [number, number, number],
  spacing: [number, number, number] | null,
  effectiveMin: number,
  effectiveMax: number,
  maxRadiusPx: number,
): [[number, number], [number, number], [number, number]] | null {
  try {
    if (!scalarData || !cols || !rows) return null;
    const [z, y, x] = voxelZYX;
    const seedRow = Math.round(y);
    const seedCol = Math.round(x);
    if (seedRow < 0 || seedRow >= rows || seedCol < 0 || seedCol >= cols) return null;

    // Snap a few pixels toward the brightest nearby voxel — clicks rarely land
    // exactly on the stone center, and the FWHM math is most stable when the
    // seed is at peak HU.
    const snapped = snapToBrightestArgmax(scalarData, cols, rows, seedRow, seedCol, 3);
    if (snapped.hu < DEFAULT_FWHM_FLOOR) {
      logger.debug(
        `[multiSeriesNn] FWHM bbox: snapped HU=${snapped.hu.toFixed(0)} below floor (${DEFAULT_FWHM_FLOOR}) — skipping`,
      );
      return null;
    }

    // Adaptive HU floor at the FWHM midpoint between background and seed.
    const adaptive = adaptiveMinHU(scalarData, cols, rows, snapped.row, snapped.col, snapped.hu);
    const minHU = Math.max(adaptive, effectiveMin);

    const mask = floodFill2D(
      scalarData,
      cols,
      rows,
      snapped.row,
      snapped.col,
      minHU,
      effectiveMax,
      maxRadiusPx,
    );
    if (!mask) return null;

    // Tight bbox over filled voxels.
    let minR = rows,
      maxR = -1,
      minC = cols,
      maxC = -1;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (mask[r * cols + c]) {
          if (r < minR) minR = r;
          if (r > maxR) maxR = r;
          if (c < minC) minC = c;
          if (c > maxC) maxC = c;
        }
      }
    }
    if (maxR < 0) return null;

    // Reject obviously-runaway masks — beyond a kidney stone's plausible size.
    const [, ySp, xSp] = spacing ?? [1, 1, 1];
    const maskHalfMm = Math.max(((maxR - minR) * (ySp || 1)) / 2, ((maxC - minC) * (xSp || 1)) / 2);
    if (maskHalfMm > FWHM_BBOX_MAX_HALF_MM) {
      logger.debug(
        `[multiSeriesNn] FWHM bbox: mask half-extent ${maskHalfMm.toFixed(1)}mm > ${FWHM_BBOX_MAX_HALF_MM}mm — likely runaway, skipping`,
      );
      return null;
    }

    const y1 = Math.max(0, minR - FWHM_BBOX_PAD_PX);
    const y2 = Math.min(rows, maxR + 1 + FWHM_BBOX_PAD_PX);
    const x1 = Math.max(0, minC - FWHM_BBOX_PAD_PX);
    const x2 = Math.min(cols, maxC + 1 + FWHM_BBOX_PAD_PX);
    const zInt = Math.round(z);

    logger.debug(
      `[multiSeriesNn] FWHM bbox: z=[${zInt},${zInt + 1}] y=[${y1},${y2}] x=[${x1},${x2}] ` +
        `(seedHU=${snapped.hu.toFixed(0)} adaptiveMin=${adaptive} mask=${maxR - minR + 1}×${maxC - minC + 1}px)`,
    );

    return [
      [zInt, zInt + 1],
      [y1, y2],
      [x1, x2],
    ];
  } catch (err) {
    console.warn("[multiSeriesNn] Failed to compute FWHM-adaptive bbox:", err);
    return null;
  }
}

/**
 * Find the brightest-HU pixel inside a projected bbox on a companion slice.
 *
 * Used as the FWHM seed for companion calls. The world→voxel projection from
 * the axial click can land in air on the companion (small alignment offsets
 * between the axial primary and coronal/sagittal reformat series), so a
 * 15-voxel snap from `snapCompanionSeed` isn't enough to find the stone.
 *
 * Searches a small Z window (±COMPANION_Z_SEARCH_RADIUS slices) around the
 * projected slice — for some series the stone genuinely sits on a slightly
 * different slice in companion space than the world projection predicts.
 * Inside each slice, argmax over HU within the projected primary-contour
 * bbox lands on the stone's peak voxel.
 *
 * Returns [Z, Y, X] in companion voxel space (Z = the slice we found the
 * stone on), or null when no pixel ≥ stoneFloor is anywhere in the search
 * window.
 */
const COMPANION_Z_SEARCH_RADIUS = 2;

async function findCompanionStoneSeedInBbox(
  companionViewport: any,
  searchBbox: [[number, number], [number, number], [number, number]],
  stoneFloor: number,
): Promise<[number, number, number] | null> {
  try {
    const [[zCenter], [y1, y2], [x1, x2]] = searchBbox;
    const imageIds: string[] = companionViewport.getImageIds?.() ?? [];
    if (imageIds.length === 0) return null;

    const zStart = Math.max(0, zCenter - COMPANION_Z_SEARCH_RADIUS);
    const zEnd = Math.min(imageIds.length, zCenter + COMPANION_Z_SEARCH_RADIUS + 1);

    // The incoming searchBbox is in vtk-frame voxel coords. To read pixelData
    // we must operate in the loaded slice's DICOM frame. Map the 4 vtk bbox
    // corners to world via vtk indexToWorld, then to DICOM (r, c) on the
    // loaded image. Falls back to vtk-frame-as-pixel-coords if metadata missing.
    const vtkImg = companionViewport.getImageData?.()?.imageData;

    let bestHU = -Infinity;
    let bestRow = -1,
      bestCol = -1,
      bestZ = -1;
    let bestDicomFrame = false;

    for (let z = zStart; z < zEnd; z++) {
      const image = await _cornerstone.imageLoader.loadAndCacheImage(imageIds[z]);
      const pixelData = image?.getPixelData?.();
      const cols = image?.columns;
      const rows = image?.rows;
      if (!pixelData || !cols || !rows) continue;

      const slope = image.slope ?? 1;
      const intercept = image.intercept ?? 0;

      let rMin: number, rMax: number, cMin: number, cMax: number, dicomFrame: boolean;
      const cornerVtkPts: [number, number, number][] = [
        [x1, y1, z],
        [x2 - 1, y1, z],
        [x1, y2 - 1, z],
        [x2 - 1, y2 - 1, z],
      ];
      // Fetch slice geometry once; reuse across the 4 corner conversions.
      const sliceGeom = vtkImg ? getImagePlaneGeometry(imageIds[z], image) : null;
      const dicomCorners = sliceGeom
        ? cornerVtkPts
            .map((vtkIdx) => vtkImg.indexToWorld(vtkIdx) as Pt3 | null)
            .map((world) => (world ? worldToImagePixelGeom(world, sliceGeom) : null))
        : null;
      if (dicomCorners && dicomCorners.every((p) => p !== null)) {
        const rs = dicomCorners.map((p) => p!.r);
        const cs = dicomCorners.map((p) => p!.c);
        rMin = Math.max(0, Math.floor(Math.min(...rs)));
        rMax = Math.min(rows, Math.ceil(Math.max(...rs)) + 1);
        cMin = Math.max(0, Math.floor(Math.min(...cs)));
        cMax = Math.min(cols, Math.ceil(Math.max(...cs)) + 1);
        dicomFrame = true;
      } else {
        rMin = Math.max(0, y1);
        rMax = Math.min(rows, y2);
        cMin = Math.max(0, x1);
        cMax = Math.min(cols, x2);
        dicomFrame = false;
      }

      for (let r = rMin; r < rMax; r++) {
        for (let c = cMin; c < cMax; c++) {
          const hu = (pixelData[r * cols + c] as number) * slope + intercept;
          if (hu > bestHU) {
            bestHU = hu;
            bestRow = r;
            bestCol = c;
            bestZ = z;
            bestDicomFrame = dicomFrame;
          }
        }
      }
    }

    if (bestRow < 0 || bestHU < stoneFloor) {
      logger.debug(
        `[multiSeriesNn] Companion seed search: no pixel ≥ ${stoneFloor} HU in bbox ` +
          `z=[${zStart},${zEnd}) y=[${y1},${y2}) x=[${x1},${x2}) (best HU=${bestHU.toFixed(0)})`,
      );
      return null;
    }
    logger.debug(
      `[multiSeriesNn] Companion seed search: found HU=${bestHU.toFixed(0)} at [z=${bestZ},r=${bestRow},c=${bestCol}] ` +
        `${bestDicomFrame ? "(DICOM frame) " : ""}(searched z=[${zStart},${zEnd}) y=[${y1},${y2}) x=[${x1},${x2}))`,
    );

    if (bestDicomFrame) {
      const bestImageId = imageIds[bestZ];
      const image = await _cornerstone.imageLoader.loadAndCacheImage(bestImageId);
      const vtkVoxel = dicomPixelToVtkVoxel(bestRow, bestCol, bestZ, bestImageId, image, vtkImg);
      if (vtkVoxel) return vtkVoxel;
    }
    return [bestZ, bestRow, bestCol];
  } catch (err) {
    console.warn("[multiSeriesNn] Failed to find companion stone seed in bbox:", err);
    return null;
  }
}

/** Wider 2D argmax search for a stone-density voxel near the projected click.
 *
 *  The world→voxel projection from axial primary lands a few voxels off the
 *  stone in companion space (axial-vs-reformat alignment offset). The 3-voxel
 *  argmax inside `computeFwhmAdaptiveBbox` can't bridge that gap, so FWHM
 *  always bails on companions with "below floor" even when the actual stone
 *  is just a few mm away. A larger 2D snap finds it.
 *
 *  Stays on the seed's slice — searching across slices risks jumping to a
 *  different stone or contrast-enhanced parenchyma. Returns null if no pixel
 *  ≥ stoneFloor exists within radius.
 */
const COMPANION_FWHM_SEED_SEARCH_RADIUS = 8; // voxels — ~7.5 mm at 0.94 mm/voxel

async function findCompanionStoneSeedNearPoint(
  companionViewport: any,
  seedZYX: [number, number, number],
  searchRadius: number,
  stoneFloor: number,
  worldPt: [number, number, number],
): Promise<[number, number, number] | null> {
  try {
    const [z, y, x] = seedZYX;
    const imageIds: string[] = companionViewport.getImageIds?.() ?? [];
    if (z < 0 || z >= imageIds.length) return null;

    const seedImageId = imageIds[z];
    const image = await _cornerstone.imageLoader.loadAndCacheImage(seedImageId);
    const pixelData = image?.getPixelData?.();
    const cols = image?.columns;
    const rows = image?.rows;
    if (!pixelData || !cols || !rows) return null;

    const slope = image.slope ?? 1;
    const intercept = image.intercept ?? 0;

    const dicom = worldToImagePixel(worldPt, seedImageId, image);
    const cy = dicom ? dicom.r : y;
    const cx = dicom ? dicom.c : x;

    const imgData = companionViewport.getImageData?.();
    const ipm = _cornerstone.metaData.get("imagePlaneModule", seedImageId);
    const vtkRowSp = imgData?.spacing?.[1] ?? ipm?.rowPixelSpacing ?? image.rowPixelSpacing ?? 1;
    const dicomRowSp = ipm?.rowPixelSpacing ?? image.rowPixelSpacing ?? vtkRowSp;
    const dicomColSp =
      ipm?.columnPixelSpacing ?? image.columnPixelSpacing ?? imgData?.spacing?.[0] ?? 1;
    const { radiusR, radiusC } = dicom
      ? scaleRadiusToDicomFrame(searchRadius, vtkRowSp, dicomRowSp, dicomColSp)
      : { radiusR: searchRadius, radiusC: searchRadius };

    let bestHU = -Infinity;
    let bestRow = -1,
      bestCol = -1;
    const rMin = Math.max(0, Math.round(cy) - radiusR);
    const rMax = Math.min(rows, Math.round(cy) + radiusR + 1);
    const cMin = Math.max(0, Math.round(cx) - radiusC);
    const cMax = Math.min(cols, Math.round(cx) + radiusC + 1);
    for (let r = rMin; r < rMax; r++) {
      for (let c = cMin; c < cMax; c++) {
        const dr = (r - cy) / radiusR;
        const dc = (c - cx) / radiusC;
        if (dr * dr + dc * dc > 1) continue;
        const hu = (pixelData[r * cols + c] as number) * slope + intercept;
        if (hu > bestHU) {
          bestHU = hu;
          bestRow = r;
          bestCol = c;
        }
      }
    }

    if (bestRow < 0 || bestHU < stoneFloor) {
      logger.debug(
        `[multiSeriesNn] Companion seed (radius=${searchRadius}${dicom ? `→DICOM r=${radiusR},c=${radiusC}` : ""}): ` +
          `no pixel ≥ ${stoneFloor} HU near [z=${z},center=${cy.toFixed(0)},${cx.toFixed(0)}] (best HU=${bestHU.toFixed(0)})`,
      );
      return null;
    }
    logger.debug(
      `[multiSeriesNn] Companion seed (radius=${searchRadius}${dicom ? "→DICOM" : ""}): found HU=${bestHU.toFixed(0)} at ` +
        `[z=${z},r=${bestRow},c=${bestCol}]${dicom ? " (DICOM frame)" : ""}`,
    );

    if (dicom) {
      const vtkVoxel = dicomPixelToVtkVoxel(
        bestRow,
        bestCol,
        z,
        seedImageId,
        image,
        companionViewport.getImageData?.()?.imageData,
      );
      if (vtkVoxel) return vtkVoxel;
    }
    return [z, bestRow, bestCol];
  } catch (err) {
    console.warn("[multiSeriesNn] Failed to find companion stone seed near point:", err);
    return null;
  }
}

/**
 * Fire companion (non-primary) series calls in parallel.
 */
async function fireCompanionCalls(
  companions: Array<{
    plane: PlaneKey;
    seriesUid: string;
    viewport: any;
    voxelZYX: [number, number, number];
    spacing: [number, number, number] | null;
  }>,
  label: "positive" | "negative",
  companionVpIds: string[],
  clickWorldPoint: Pt3,
  cachedContext: CachedContext | null,
  segmentIndex: number,
  clickedPlane: PlaneKey,
  requestContext: NonNullable<typeof _nnContext>,
  isCurrentSession: () => boolean,
  pendingRollbacks: Map<PlaneKey, () => void>,
  committedPlanes: Set<PlaneKey>,
  primaryViewport?: any,
  primaryContours?: Record<string, number[][][]>,
): Promise<void> {
  if (!isCurrentSession()) return;

  _setNnLoading?.({ isLoading: true, viewportId: null, companionViewportIds: companionVpIds });

  const pendingVpIds = new Set(companionVpIds);

  const results = await Promise.allSettled(
    companions.map(async (comp) => {
      try {
        if (!isCurrentSession()) return;
        // Initial seed: world-projected click + 15-voxel brightness snap. On
        // some reformat series this lands in air (axial-original and coronal/
        // sagittal reformats can be slightly misaligned), so we re-snap below
        // by searching the projected primary-contour bbox.
        const initialSnap = await snapCompanionSeed(comp.viewport, comp.voxelZYX, clickWorldPoint);
        if (!isCurrentSession()) return;

        // Project the primary mask into companion space and search inside that
        // projected bbox for the stone's peak HU pixel. Guarantees an on-stone
        // seed for FWHM regardless of the world-coord misalignment, since the
        // bbox is derived from primary contours that already trace the stone.
        const projectedBbox =
          primaryViewport && primaryContours
            ? computeCompanionBbox(
                primaryViewport,
                primaryContours,
                comp.viewport,
                Math.round(initialSnap[0]),
              )
            : null;
        const stoneSeed = projectedBbox
          ? await findCompanionStoneSeedInBbox(comp.viewport, projectedBbox, DEFAULT_FWHM_FLOOR)
          : null;
        if (!isCurrentSession()) return;
        const snappedVoxel: [number, number, number] = stoneSeed ?? initialSnap;

        // Bounds check: if the world-projected voxel falls outside the
        // companion volume's Z range, the click world coord doesn't actually
        // intersect this series (typically a backend metadata misalignment —
        // mismatched IPP origins between axial and companion reformats).
        // Sending an out-of-bounds seed to the API yields a 4xx + AxiosError;
        // skip the call and surface the misalignment as a debug log so the
        // user gets a clean primary contour without a confusing companion
        // failure or wrong-slice jump.
        const compNumSlices = comp.viewport.getImageIds?.()?.length ?? 0;
        const seedZ = snappedVoxel[0];
        if (compNumSlices > 0 && (seedZ < 0 || seedZ >= compNumSlices)) {
          logger.warn(
            `[multiSeriesNn] Companion (${comp.plane}) skipped: projected voxel z=${seedZ} ` +
              `outside volume [0, ${compNumSlices}) — series IPP misalignment`,
            { plane: comp.plane, seedZ, nSlices: compNumSlices, clickWorldPoint },
          );
          pendingVpIds.delete(comp.viewport?.id);
          showNnToast(
            `${comp.plane} view can't be projected — series alignment issue. Click on each viewport directly.`,
          );
          return { plane: comp.plane, response: null };
        }

        // Prepare the click marker, but publish it only after the mask succeeds.
        let publishMarker = () => {};
        try {
          const compImgData = comp.viewport.getImageData?.();
          const vtkImg = compImgData?.imageData;
          if (vtkImg && _setNnClickMarkers) {
            const worldPt = vtkImg.indexToWorld([
              snappedVoxel[2],
              snappedVoxel[1],
              snappedVoxel[0],
            ]);
            const compImageIds: string[] = comp.viewport.getImageIds?.() ?? [];
            const seedSlice = Math.round(snappedVoxel[0]);
            const compImageId =
              compImageIds[seedSlice] ?? comp.viewport.getCurrentImageId?.() ?? "";
            publishMarker = () =>
              _setNnClickMarkers?.((prev) => [
                ...prev,
                {
                  worldPoint: [worldPt[0], worldPt[1], worldPt[2]] as Pt3,
                  label,
                  viewportId: comp.viewport?.id ?? "",
                  imageId: compImageId,
                },
              ]);
          }
        } catch {
          /* marker is cosmetic */
        }

        const hadSession = _multiSessions.has(comp.plane);
        const session = getOrCreateSession(comp.plane, comp.seriesUid, comp.viewport, comp.spacing);
        const previous = snapshotSession(session);
        pendingRollbacks.set(comp.plane, () => {
          restoreSessionState(session, previous);
          if (!hadSession) _multiSessions.delete(comp.plane);
          syncSessionActive();
        });
        // Plain grow starts a fresh prediction on every selected plane.
        // Preserve the previous session in its snapshot for failure and Undo.
        session.interactions = [];
        session.sessionId = null;

        // Prepend a bbox interaction to constrain the companion's search region
        // to the primary's stone area (prevents segmenting the whole kidney).
        //
        // Run the same FWHM flow as primary, just on companion data:
        //   1. Wider 2D argmax snap (8 voxels ≈ 7.5 mm) to bridge the axial-vs-
        //      reformat alignment offset that leaves the projected click in air
        //      a few voxels off the stone.
        //   2. If the snap found a stone-density voxel, run FWHM region-grow on
        //      the companion's own pixel data → bbox traces companion stone
        //      voxels with 3px padding, identical shape to primary.
        // Falls back to the projected primary-contour bbox when no stone-density
        // voxel exists within the wider search radius.
        const fwhmSeed = cachedContext
          ? await findCompanionStoneSeedNearPoint(
              comp.viewport,
              snappedVoxel,
              COMPANION_FWHM_SEED_SEARCH_RADIUS,
              DEFAULT_FWHM_FLOOR,
              clickWorldPoint,
            )
          : null;
        if (!isCurrentSession()) return;
        const fwhmBbox =
          cachedContext && fwhmSeed
            ? await computeCompanionFwhmAdaptiveBbox(
                comp.viewport,
                fwhmSeed,
                comp.spacing,
                cachedContext.effectiveMin,
                cachedContext.effectiveMax,
                cachedContext.maxRadiusPx,
                clickWorldPoint,
              )
            : null;
        if (!isCurrentSession()) return;
        const bbox = fwhmBbox ?? projectedBbox;
        if (bbox) {
          session.interactions.push({
            type: "bbox",
            bbox,
            label: "positive",
          });
        }

        const interaction: NnInteractiveInteraction = {
          type: "point",
          coordinates: snappedVoxel,
          label,
        };
        session.interactions.push(interaction);

        const response = await nnInteractiveSegment({
          project_id: requestContext.projectId,
          study_uid: requestContext.studyUid,
          series_uid: comp.seriesUid,
          interactions: session.interactions,
          session_id: session.sessionId,
          reset: false,
          ...(comp.spacing ? { spacing: comp.spacing } : {}),
        });
        if (!isCurrentSession()) return;

        // Volume guard for companion planes — reject oversized segmentations
        // (likely whole kidney instead of stone). Organ mode raises the ceiling so
        // a deliberate whole-organ result is accepted rather than discarded.
        const compVolCeiling = requestContext.isStone ? MAX_STONE_VOLUME_MM3 : MAX_ORGAN_VOLUME_MM3;
        if (response.volume_mm3 > compVolCeiling) {
          console.warn(
            `[multiSeriesNn] Companion (${comp.plane}): oversized ${response.volume_mm3.toFixed(1)} mm³ ` +
              `(max ${compVolCeiling}) — discarding contours`,
          );
          pendingRollbacks.get(comp.plane)?.();
          pendingRollbacks.delete(comp.plane);
          pendingVpIds.delete(comp.viewport?.id);
          return { plane: comp.plane, response: null };
        }

        session.sessionId = response.session_id;
        smoothNnContourMap(response.contours);
        smoothNnContourMap(response.coronal_contours);
        smoothNnContourMap(response.sagittal_contours);
        session.lastResponse = response;

        applyNnGeometry(session, previous, response, cachedContext, segmentIndex);
        session.undoStack.push(previous);
        committedPlanes.add(comp.plane);
        pendingRollbacks.delete(comp.plane);
        publishMarker();

        pendingVpIds.delete(comp.viewport?.id);
        const remaining = Array.from(pendingVpIds);
        if (remaining.length > 0) {
          _setNnLoading?.({ isLoading: true, viewportId: null, companionViewportIds: remaining });
        } else {
          _setNnLoading?.({ isLoading: false, viewportId: null });
        }

        logger.debug(
          `[multiSeriesNn] Companion (${comp.plane}): ${Object.keys(response.contours).length} slices, ${response.volume_mm3.toFixed(1)} mm³`,
        );
        return { plane: comp.plane, response };
      } catch (error) {
        if (isCurrentSession()) {
          pendingRollbacks.get(comp.plane)?.();
          pendingRollbacks.delete(comp.plane);
        }
        throw error;
      }
    }),
  );

  if (!isCurrentSession()) return;

  let companionFailCount = 0;
  for (const result of results) {
    if (result.status === "rejected") {
      console.warn("[multiSeriesNn] Companion call failed:", result.reason);
      companionFailCount++;
    } else if (result.status === "fulfilled" && result.value?.response === null) {
      // Volume guard rejected this companion
      companionFailCount++;
    }
  }
  if (companionFailCount > 0) {
    showNnToast(`Companion views unavailable (${companionFailCount} rejected)`);
  }

  _setNnLoading?.({ isLoading: false, viewportId: null });

  // Snap companion viewports to the seed slice (green dot) rather than middle contour slice.
  // Skip the clicked plane to avoid moving the user away from where they clicked.
  {
    const sibs = _getSiblingViewports?.();
    if (sibs) {
      // Build map of plane → seed slice Z from companion specs
      const seedSliceByPlane = new Map<PlaneKey, number>();
      for (const comp of companions) {
        seedSliceByPlane.set(comp.plane, Math.round(comp.voxelZYX[0]));
      }
      const seedSliceNormalized = (plane: PlaneKey): number => {
        const vp = sibs[plane]?.viewport;
        if (!vp) return 0.5;
        const ids: string[] = vp.getImageIds?.() ?? [];
        if (ids.length <= 1) return 0.5;
        const seedZ = seedSliceByPlane.get(plane);
        if (seedZ != null) return seedZ / (ids.length - 1);
        return 0.5;
      };
      const getCurrentNormalized = (plane: PlaneKey): number => {
        const vp = sibs[plane]?.viewport;
        if (!vp) return 0.5;
        const ids: string[] = vp.getImageIds?.() ?? [];
        if (ids.length <= 1) return 0.5;
        // getCurrentImageIdIndex returns K-step. On reversed-HTJ2K canonicalized
        // volumes K-step and list-index differ by N-1-i, and HangingProtocolGrid's
        // bus handler treats this value as list-index — feeding raw K-step makes
        // the scrollbar jump to the mirrored slice, then snap back once a second
        // sync arrives. Resolve through getCurrentImageId → imageIds.indexOf to
        // stay in list-index frame (same pattern as MeasureClickTool.ts).
        const currentImageId: string | undefined = vp.getCurrentImageId?.();
        const listIdx = currentImageId ? ids.indexOf(currentImageId) : -1;
        if (listIdx < 0) {
          logger.warn(
            "[multiSeriesNn] getCurrentNormalized: currentImageId not in imageIds for plane=%s; falling back to K-step",
            plane,
          );
          const idx = vp.getCurrentImageIdIndex?.() ?? 0;
          return idx / (ids.length - 1);
        }
        return listIdx / (ids.length - 1);
      };
      notifyNnCrossViewportSync({
        axial:
          clickedPlane === "axial" ? getCurrentNormalized("axial") : seedSliceNormalized("axial"),
        coronal:
          clickedPlane === "coronal"
            ? getCurrentNormalized("coronal")
            : seedSliceNormalized("coronal"),
        sagittal:
          clickedPlane === "sagittal"
            ? getCurrentNormalized("sagittal")
            : seedSliceNormalized("sagittal"),
      });
    }
  }

  updateMultiSeries3DViewer();
}

/**
 * Multi-series nnInteractive click handler.
 * Converts world-space click to each series' native voxel coords,
 * fires primary series first (fast UX), then companions in parallel.
 */
export async function handleMultiSeriesNnClick(
  worldPoint: Pt3,
  clickedPlane: PlaneKey,
  label: "positive" | "negative",
  ctx: CachedContext,
  segmentIndex: number,
  regionGrowImageIds: Set<string>,
  onRegionGrowCommit: ((currentImageId: string | null) => void) | null,
  onRegionGrowAutoSwitch: (() => void) | null,
  isRefinement: boolean = false,
): Promise<boolean> {
  if (!_nnContext || _nnRequestInFlight || !_cornerstone) return false;

  const requestGeneration = _nnSessionGeneration;
  const requestContext = { ..._nnContext };
  let cancelled = false;
  const isCurrentSession = () => !cancelled && requestGeneration === _nnSessionGeneration;
  const pendingRollbacks = new Map<PlaneKey, () => void>();
  const committedPlanes = new Set<PlaneKey>();
  const finishLoading = () => {
    if (_cancelPendingNnOperation !== cancelOperation) return;
    _cancelPendingNnOperation = null;
    _nnRequestInFlight = false;
    _setNnLoading?.({ isLoading: false, viewportId: null });
  };
  const cancelOperation = () => {
    if (!isCurrentSession()) return;
    cancelled = true;
    for (const rollback of pendingRollbacks.values()) rollback();
    pendingRollbacks.clear();
    finishLoading();
  };

  const siblings = _getSiblingViewports?.();
  if (!siblings) return false;

  // Diagnostic: log Frame-of-Reference UID per sibling plane. Cross-plane
  // projection requires shared FOR (or a known transform). Mismatched FORs
  // here explain "click landed inside companion volume bounds visually but
  // out of bounds per IPP metadata" — the volumes describe the same
  // anatomy but in different spatial coordinate systems.
  const forSummary = PLANE_KEYS.map((p) => {
    const v = siblings[p]?.viewport;
    const forUID = v?.getFrameOfReferenceUID?.() ?? "<missing>";
    const seriesUid = siblings[p]?.seriesUid ?? "<no-series>";
    const vtkImg = v?.getImageData?.()?.imageData;
    const bounds: number[] | undefined = vtkImg?.getBounds?.();
    const boundsStr = bounds
      ? `[${bounds.map((b: number) => b.toFixed(1)).join(",")}]`
      : "<no-bounds>";
    return `${p}: for=…${forUID.slice(-12)} series=…${seriesUid.slice(-8)} bounds=${boundsStr}`;
  }).join(" || ");
  logger.debug(
    `[multiSeriesNn] FOR diagnostic on ${clickedPlane} click @ world=[${worldPoint.map((v) => v.toFixed(1)).join(",")}]: ${forSummary}`,
  );

  const isFirstClick = _multiSessions.size === 0;
  const viewportId = ctx.viewport.id;

  // Determine which planes to call
  const planesToCall: PlaneKey[] = isRefinement
    ? [clickedPlane]
    : PLANE_KEYS.filter((p) => siblings[p]?.seriesUid != null);

  // Convert world coords to each series' native voxel space
  const callSpecs: Array<{
    plane: PlaneKey;
    seriesUid: string;
    viewport: any;
    voxelZYX: [number, number, number];
    spacing: [number, number, number] | null;
  }> = [];
  const seenSeriesUids = new Set<string>();
  // Companion planes we could NOT project the click into (their reconstructed
  // series lacked the IPP/IOP/spacing needed by worldToNativeVoxelZYX). Tracked
  // so we can tell the annotator WHY that view didn't auto-fill instead of
  // leaving it silently blank — the failure is data-dependent (per study).
  const projectionSkipped: string[] = [];

  for (const plane of planesToCall) {
    const sib = siblings[plane];
    if (!sib?.viewport || !sib?.seriesUid) {
      continue;
    }
    if (seenSeriesUids.has(sib.seriesUid)) {
      continue;
    }
    seenSeriesUids.add(sib.seriesUid);

    const voxel = worldToNativeVoxelZYX(worldPoint, sib.viewport, _cornerstone);
    if (!voxel) {
      projectionSkipped.push(plane);
      continue;
    }
    // No `getCurrentImageIdIndex` override here on purpose. On VolumeViewport
    // with progressive HTJ2K loading, that counter drifts from the rendered
    // camera plane (5-slice gap observed in practice), and previously we were
    // forcing the segmentation request onto the *advertised* slice instead of
    // the slice the user actually clicked — the resulting mask landed several
    // slices away from the click. `worldToNativeVoxelZYX` resolves the click
    // world point against IPP/IOP and matches what the user sees on screen.

    const sp = getSpacingZYX(sib.viewport, _cornerstone);
    logger.debug(
      `[multiSeriesNn] ${plane}: world=[${worldPoint.map((v) => v.toFixed(1))}] → voxel=[${voxel}] spacing=${sp ? `[${sp.map((v) => v.toFixed(2))}]` : "null"} series=${sib.seriesUid.slice(-8)}`,
    );
    callSpecs.push({
      plane,
      seriesUid: sib.seriesUid,
      viewport: sib.viewport,
      voxelZYX: voxel,
      spacing: sp,
    });
  }

  // Notify when a companion view couldn't be auto-filled because its series
  // lacked the slice-position metadata to project the click. Naming the view +
  // reason lets the annotator click that view directly instead of assuming the
  // tool is broken. Excludes the clicked plane (its own result is visible).
  const skippedCompanions = projectionSkipped.filter((p) => p !== clickedPlane);
  if (skippedCompanions.length > 0) {
    const names = skippedCompanions.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" & ");
    showNnToast(
      `${names} view couldn't auto-fill (missing slice-position data for that series). Click directly on it to segment there.`,
    );
  }

  if (callSpecs.length === 0) return false;

  const primaryIdx = callSpecs.findIndex((s) => s.plane === clickedPlane);
  const primary = primaryIdx >= 0 ? callSpecs.splice(primaryIdx, 1)[0] : callSpecs.shift()!;
  // Occupied companions participate in plain grows too. Their checkpoints
  // preserve existing geometry, including manual edits, for one-action Undo.
  // Refinement selected only the clicked plane above, so has no companions.
  const companions = callSpecs;

  _nnRequestInFlight = true;
  _cancelPendingNnOperation = cancelOperation;
  const companionVpIds = companions.map((c) => c.viewport?.id).filter(Boolean) as string[];
  _setNnLoading?.({ isLoading: true, viewportId, companionViewportIds: companionVpIds });
  let companionsFired = false;
  let geometryApplyStarted = false;

  try {
    const hadSession = _multiSessions.has(primary.plane);
    const primarySession = getOrCreateSession(
      primary.plane,
      primary.seriesUid,
      primary.viewport,
      primary.spacing,
    );

    const previous = snapshotSession(primarySession);
    pendingRollbacks.set(primary.plane, () => {
      restoreSessionState(primarySession, previous);
      if (!hadSession) _multiSessions.delete(primary.plane);
      syncSessionActive();
    });

    const startsNewPrediction = !isRefinement || !primarySession.sessionId;
    if (!isRefinement) {
      // A new seed replaces the AI conversation, not the geometry Undo history.
      // Snapshot first so Undo (or request failure) can restore the old session.
      primarySession.interactions = [];
      primarySession.sessionId = null;
    }

    // Always include a tight bbox alongside the seed point on the FIRST click
    // (not on refinement — that would blow away the user's prior interactions).
    // Without this spatial hint the model floods across kidney parenchyma and
    // returns whole-organ segmentations.
    //
    // Try the FWHM-adaptive region-grow bbox first — it traces the actual
    // high-HU stone voxels, giving a much tighter prior than a fixed cube. Fall
    // back to the ±PRIMARY_BBOX_HALF_MM cube when scalar data is unavailable
    // (e.g. companion-only context) or the FWHM fill produces no usable mask
    // (seed below stone floor, runaway leak, etc).
    let primaryFwhmBbox: [[number, number], [number, number], [number, number]] | null = null;
    // Stone mode only: apply the tight stone-focusing seed bbox. That bbox
    // exists to STOP nnInteractive flooding into kidney parenchyma when hunting
    // a small stone. Every non-stone category (organ, lymph node, lesions) skips
    // it and lets the bare seed point grow the full structure.
    if (startsNewPrediction && requestContext.isStone) {
      primaryFwhmBbox =
        ctx.scalarData && ctx.cols && ctx.rows
          ? computeFwhmAdaptiveBbox(
              ctx.scalarData,
              ctx.cols,
              ctx.rows,
              primary.voxelZYX,
              primary.spacing,
              ctx.effectiveMin,
              ctx.effectiveMax,
              ctx.maxRadiusPx,
            )
          : null;
      const initialBbox =
        primaryFwhmBbox ??
        computePrimaryFallbackBbox(primary.viewport, primary.voxelZYX, primary.spacing);
      if (initialBbox) {
        primarySession.interactions.push({
          type: "bbox",
          bbox: initialBbox,
          label: "positive",
        });
      }
    }

    const newInteraction: NnInteractiveInteraction = {
      type: "point",
      coordinates: primary.voxelZYX,
      label,
    };
    primarySession.interactions.push(newInteraction);

    const primaryImageId = primary.viewport?.getCurrentImageId?.() ?? "";

    const primaryResponse = await nnInteractiveSegment({
      project_id: requestContext.projectId,
      study_uid: requestContext.studyUid,
      series_uid: primary.seriesUid,
      interactions: primarySession.interactions,
      session_id: primarySession.sessionId,
      reset: false,
      ...(primary.spacing ? { spacing: primary.spacing } : {}),
    });

    // Discard stale work without triggering the caller's local fallback.
    if (!isCurrentSession()) return true;

    primarySession.sessionId = primaryResponse.session_id;
    smoothNnContourMap(primaryResponse.contours);
    smoothNnContourMap(primaryResponse.coronal_contours);
    smoothNnContourMap(primaryResponse.sagittal_contours);
    primarySession.lastResponse = primaryResponse;

    // Volume guard: reject oversized segmentations (likely whole organ, not stone).
    // Apply to fresh predictions — refinement clicks intentionally modify the existing
    // session and the volume may fluctuate. Blocking refinement is confusing to the user.
    const volCeiling = requestContext.isStone ? MAX_STONE_VOLUME_MM3 : MAX_ORGAN_VOLUME_MM3;
    const guardOutcome = startsNewPrediction
      ? classifyVolumeGuard(primaryResponse.volume_mm3, _consecutiveOversizedCount, volCeiling)
      : "accept";
    if (guardOutcome !== "accept") {
      _consecutiveOversizedCount++;
      console.warn(
        `[multiSeriesNn] Oversized segmentation: ${primaryResponse.volume_mm3.toFixed(1)} mm³ ` +
          `(max ${volCeiling}) — attempt ${_consecutiveOversizedCount}`,
      );

      // A rejected response has not committed geometry or an Undo snapshot.
      pendingRollbacks.get(primary.plane)?.();
      pendingRollbacks.delete(primary.plane);

      if (guardOutcome === "reject_fallback") {
        if (previous.contourUIDs.length > 0) {
          showNnToast("Segmentation too large. Previous contour kept; please try again.");
          _consecutiveOversizedCount = 0;
          return true;
        }
        // Two consecutive oversized results → fall back to local flood fill
        showNnToast("Segmentation too large twice — falling back to local method");
        _nnFailedAt = Date.now();
        _consecutiveOversizedCount = 0;
        syncSessionActive();
        updateMultiSeries3DViewer();
        return false;
      }
      // reject_retry — keep the strike count and the original contours.
      showNnToast("Segmentation too large — please try again");
      syncSessionActive();
      updateMultiSeries3DViewer();
      return true; // Return true to indicate we handled it (not a fallback trigger)
    }

    // Volume is acceptable — reset consecutive counter
    _consecutiveOversizedCount = 0;

    geometryApplyStarted = true;
    applyNnGeometry(primarySession, previous, primaryResponse, ctx, segmentIndex);
    primarySession.undoStack.push(previous);
    committedPlanes.add(primary.plane);
    pendingRollbacks.delete(primary.plane);

    // Add click marker AFTER successful API response (#203).
    // Previously added before the API call, which left orphan markers on failure.
    _setNnClickMarkers?.((prev) => [
      ...prev,
      {
        worldPoint: [...worldPoint] as Pt3,
        label,
        viewportId: primary.viewport?.id ?? "",
        imageId: primaryImageId,
      },
    ]);

    if (isFirstClick && _onNnLayoutSwitch) _onNnLayoutSwitch();

    const currentImageId = ctx.viewport.getCurrentImageId?.();
    if (currentImageId) regionGrowImageIds.add(currentImageId);

    if (onRegionGrowCommit) onRegionGrowCommit(currentImageId ?? null);

    if (onRegionGrowAutoSwitch) {
      const cb = onRegionGrowAutoSwitch;
      queueMicrotask(() => {
        if (isCurrentSession()) cb();
      });
    }

    // Always update 3D viewer with latest primary contours immediately.
    // If companions are in flight, it updates again when they complete.
    updateMultiSeries3DViewer();

    // Snap sibling viewports to segmentation centroid.
    // Skip the clicked plane — keep user on the slice they clicked.
    // Also skip on refinement: only the clicked plane is being modified, so
    // moving the OTHER viewports' slices is just visual noise (e.g. sagittal
    // jumping when the user is Cmd+Clicking on coronal).
    if (!isRefinement) {
      const sibs = _getSiblingViewports?.();
      if (sibs) {
        const findSliceNormalized = (vp: any): number => {
          if (!vp) return 0.5;
          const ids: string[] = vp.getImageIds?.() ?? [];
          if (ids.length <= 1) return 0.5;
          let bestIdx = 0,
            bestDist = Infinity;
          for (let i = 0; i < ids.length; i++) {
            try {
              const ipm = _cornerstone.metaData.get("imagePlaneModule", ids[i]);
              if (!ipm?.imagePositionPatient) continue;
              const ipp = ipm.imagePositionPatient;
              const dist = Math.sqrt(
                (ipp[0] - worldPoint[0]) ** 2 +
                  (ipp[1] - worldPoint[1]) ** 2 +
                  (ipp[2] - worldPoint[2]) ** 2,
              );
              if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
              }
            } catch {
              /* noop */
            }
          }
          return bestIdx / (ids.length - 1);
        };
        const getCurrentNormalized = (vp: any): number => {
          if (!vp) return 0.5;
          const ids: string[] = vp.getImageIds?.() ?? [];
          if (ids.length <= 1) return 0.5;
          // K-step vs list-index — see the post-companion getCurrentNormalized
          // above for the full explanation.
          const currentImageId: string | undefined = vp.getCurrentImageId?.();
          const listIdx = currentImageId ? ids.indexOf(currentImageId) : -1;
          if (listIdx < 0) {
            logger.warn(
              "[multiSeriesNn] getCurrentNormalized: currentImageId not in imageIds; falling back to K-step",
            );
            const idx = vp.getCurrentImageIdIndex?.() ?? 0;
            return idx / (ids.length - 1);
          }
          return listIdx / (ids.length - 1);
        };
        notifyNnCrossViewportSync({
          axial:
            clickedPlane === "axial"
              ? getCurrentNormalized(sibs.axial?.viewport)
              : findSliceNormalized(sibs.axial?.viewport),
          coronal:
            clickedPlane === "coronal"
              ? getCurrentNormalized(sibs.coronal?.viewport)
              : findSliceNormalized(sibs.coronal?.viewport),
          sagittal:
            clickedPlane === "sagittal"
              ? getCurrentNormalized(sibs.sagittal?.viewport)
              : findSliceNormalized(sibs.sagittal?.viewport),
        });
      }
    }

    logger.debug(
      `[multiSeriesNn] Primary (${primary.plane}): ` +
        `${Object.keys(primaryResponse.contours).length} slices, ` +
        `${primaryResponse.volume_mm3.toFixed(1)} mm³, ` +
        `${primaryResponse.max_diameter_mm.toFixed(1)} mm diameter` +
        (companions.length > 0 ? ` — firing ${companions.length} companion(s)` : ""),
    );

    if (companions.length > 0) {
      // Project a hybrid box into companion space:
      //  - In-plane (Y, X) extent comes from the primary FWHM bbox (tight
      //    around the stone voxels — much smaller than the model's actual
      //    contour, which can leak outside the bbox prior).
      //  - Cross-plane (Z) extent comes from the primary's *actual* segmented
      //    slice range. The FWHM bbox is single-slice, but the model often
      //    segments 2-3 slices; using just the FWHM bbox makes the projected
      //    coronal/sagittal Y axis (patient-Z) too thin.
      //
      // Synthesizing the FWHM rectangle on every segmented slice gives the
      // existing computeCompanionBbox its expected `Record<slice, contours>`
      // shape and lets it run the same world-projection math.
      const projectionSource: Record<string, number[][][]> = primaryFwhmBbox
        ? (() => {
            const [, [y1, y2], [x1, x2]] = primaryFwhmBbox;
            const corners: number[][] = [
              [y1, x1],
              [y1, x2],
              [y2, x2],
              [y2, x1],
            ];
            const segmentedSlices = Object.keys(primaryResponse.contours);
            const out: Record<string, number[][][]> = {};
            for (const sliceStr of segmentedSlices) out[sliceStr] = [corners];
            return out;
          })()
        : primaryResponse.contours;

      companionsFired = true;
      fireCompanionCalls(
        companions,
        label,
        companionVpIds,
        worldPoint,
        ctx,
        segmentIndex,
        clickedPlane,
        requestContext,
        isCurrentSession,
        pendingRollbacks,
        committedPlanes,
        primary.viewport,
        projectionSource,
      )
        .catch((err) => {
          if (!isCurrentSession()) return;
          console.warn("[multiSeriesNn] Companion calls error:", err);
          // fireCompanionCalls' inner clear at the end of Promise.allSettled
          // is what normally turns off the spinner. If the whole chain
          // rejects before that runs, clear here so the spinner doesn't
          // hang forever.
          _setNnLoading?.({ isLoading: false, viewportId: null });
        })
        .finally(finishLoading);
    }

    // Unified undo: record this whole nn action as ONE entry on the shared
    // queue so Ctrl+Z reverts it in recency order alongside lasso/bump/region-
    // grow. Companions join this entry only after committing geometry.
    // Undo cancels pending work, then restores only the successful planes.
    pushUndo({
      kind: "nnRevert",
      undo: () => {
        if (!isCurrentSession()) return;
        _cancelPendingNnOperation?.();
        cancelOperation();
        for (const p of committedPlanes) {
          try {
            undoNnInteractive(_cornerstoneTools, regionGrowImageIds, p);
          } catch (e) {
            logger.warn("[nnInteractiveSession] nnRevert plane failed:", p, e);
          }
        }
      },
    });

    return true;
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    if (!isCurrentSession()) return true;
    console.error("[multiSeriesNn] Primary call failed:", err);
    pendingRollbacks.get(primary.plane)?.();
    pendingRollbacks.delete(primary.plane);
    // A false result asks RegionGrowTool to draw a local threshold fallback.
    // Do not let that overwrite geometry preserved after a failed NN edit.
    let keepGeometry = geometryApplyStarted;
    try {
      keepGeometry ||=
        captureGeometryForViewport(primary.viewport, segmentIndex).contours.length > 0;
    } catch {
      keepGeometry = true;
    }
    if (keepGeometry) {
      showNnToast("NN edit failed. Previous contour kept; please try again.");
      return true;
    }
    console.warn(`[nnInteractive:fallback] reason=api_error detail=${reason}`);
    perfMark("nn-interactive-fallback");
    _nnFailedAt = Date.now();
    return false;
  } finally {
    if (isCurrentSession()) {
      // Keep the NN request lock until companion work settles or Undo cancels it.
      if (!companionsFired) {
        finishLoading();
      }
    }
  }
}

// ─── Undo / Reject ──────────────────────────────────────────────────────────

/**
 * The segmentIndex the session's contours belong to, read from a live contour
 * annotation BEFORE removal. Undo restore must re-create with this exact index —
 * addContourSegmentationAnnotation throws ("reading 'locked'") and the contour
 * renders invisibly if segments[segmentIndex] isn't the initialized one.
 */
function readSessionSegmentIndex(session: SeriesSession): number {
  if (!_cornerstoneTools) return 1;
  for (const uid of session.contourUIDs) {
    try {
      const ann = _cornerstoneTools.annotation.state.getAnnotation?.(uid);
      const idx = ann?.data?.segmentation?.segmentIndex;
      if (typeof idx === "number") return idx;
    } catch {
      /* try next */
    }
  }
  return 1;
}

/** Restore the checkpoint if contour replacement fails partway through. */
function applyNnGeometry(
  session: SeriesSession,
  previous: SessionSnapshot,
  response: NnInteractiveResponse,
  cachedContext: CachedContext | null,
  segmentIndex: number,
): void {
  previous.geometry = captureGeometryForViewport(session.viewport, segmentIndex);
  try {
    session.contourUIDs = createAnnotationsFromNnResponse(
      response,
      buildCtxForViewport(session.viewport, cachedContext),
      segmentIndex,
      session.plane,
      true,
    );
  } catch (error) {
    restoreGeometryCheckpoint(session.viewport, previous.geometry);
    session.viewport.render?.();
    throw error;
  }
}

export function captureGeometryForViewport(
  viewport: any,
  segmentIndex: number,
): SegmentationGeometryCheckpoint {
  const seg = _cornerstoneTools.segmentation;
  const segmentationId =
    seg.getActiveSegmentation?.(viewport.id)?.segmentationId ??
    seg.state.getSegmentations()?.[0]?.segmentationId ??
    "unknown";
  return captureSegmentationGeometry(
    _cornerstoneTools.annotation.state.getAnnotations(
      "PlanarFreehandContourSegmentationTool",
      viewport.element,
    ) ?? [],
    {
      segmentationId,
      segmentIndex,
      viewPlaneNormal: viewport.getCamera?.()?.viewPlaneNormal ?? [0, 0, 1],
    },
  );
}

export function restoreGeometryCheckpoint(
  viewport: any,
  checkpoint: SegmentationGeometryCheckpoint,
): string[] {
  // Clone before removal so the stored checkpoint remains independent of edits.
  const contours = checkpoint.contours.map(copyContourForCheckpoint);
  const { segmentationId, segmentIndex, viewPlaneNormal } = checkpoint.target;
  pruneContoursForPlane(
    _cornerstoneTools,
    viewport.element,
    segmentationId,
    segmentIndex,
    viewPlaneNormal,
    true,
  );
  for (const annotation of contours) {
    _cornerstoneTools.annotation.state.addAnnotation(annotation, viewport.element);
    _cornerstoneTools.utilities.contourSegmentation.addContourSegmentationAnnotation(annotation);
  }
  return contours.map((annotation) => annotation.annotationUID!);
}

/** Threshold uses these sessions for 3D display, but must preserve NN history. */
export function captureNnStateForThresholdUndo(): () => void {
  const before = Array.from(_multiSessions, ([plane, session]) => ({
    plane,
    session,
    state: {
      ...session,
      interactions: structuredClone(session.interactions),
      contourUIDs: [...session.contourUIDs],
      clickMarkers: [...session.clickMarkers],
      lastResponse: structuredClone(session.lastResponse),
      undoStack: [...session.undoStack],
    },
  }));
  return () => {
    _cancelPendingNnOperation?.();
    _multiSessions.clear();
    for (const { plane, session, state } of before) {
      Object.assign(session, state);
      _multiSessions.set(plane, session);
    }
    syncSessionActive();
    refreshViewerFromAnnotations();
  };
}

/** Prefer exact live geometry; retain response reconstruction for older paths. */
function restoreContourFromSnapshot(
  session: SeriesSession,
  prev: SessionSnapshot,
  segmentIndex: number,
): void {
  if (prev.geometry) {
    session.contourUIDs = restoreGeometryCheckpoint(session.viewport, prev.geometry);
    return;
  }
  if (!prev.response || !session.viewport || !_cornerstoneTools) {
    session.contourUIDs = [];
    return;
  }
  try {
    const seg = _cornerstoneTools.segmentation;
    const segId =
      seg.getActiveSegmentation?.(session.viewport.id)?.segmentationId ??
      seg.state.getSegmentations?.()?.[0]?.segmentationId;
    if (segId) seg.segmentIndex?.setActiveSegmentIndex?.(segId, segmentIndex);
    const ctx = buildCtxForViewport(session.viewport, null);
    session.contourUIDs = createAnnotationsFromNnResponse(
      prev.response,
      ctx,
      segmentIndex,
      session.plane,
    );
  } catch (err) {
    logger.warn("[nnUndo] restore from snapshot failed; clearing:", err);
    session.contourUIDs = [];
  }
}

/**
 * Undo the last nnInteractive refinement. Restores cached contours
 * from the undo stack without making an API call.
 * Returns true if something was undone.
 */
export function undoNnInteractive(
  cornerstoneTools: any,
  regionGrowImageIds: Set<string>,
  plane?: PlaneKey,
): boolean {
  _cancelPendingNnOperation?.();
  if (plane) {
    const session = _multiSessions.get(plane);
    if (!session || session.undoStack.length === 0) return false;

    const prev = session.undoStack.pop()!;
    const restoreSegIdx = readSessionSegmentIndex(session);

    for (const uid of prev.geometry ? [] : session.contourUIDs) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(uid);
      } catch {
        /* noop */
      }
    }

    for (let i = 0; i < 2 && session.clickMarkers.length > 0; i++) {
      const uid = session.clickMarkers.pop()!;
      try {
        cornerstoneTools.annotation.state.removeAnnotation(uid);
      } catch {
        /* noop */
      }
    }

    if (prev.interactions.length === 0 && !prev.sessionId) {
      if (prev.geometry) restoreGeometryCheckpoint(session.viewport, prev.geometry);
      for (const uid of session.clickMarkers) {
        try {
          cornerstoneTools.annotation.state.removeAnnotation(uid);
        } catch {
          /* noop */
        }
      }
      _multiSessions.delete(plane);

      // Check if remaining sessions are orphaned companions — no contours
      // and no user interactions. If so, clear them so hasNnInteractiveSession()
      // returns false and the grow tool can be re-enabled.
      if (_multiSessions.size > 0) {
        let allOrphaned = true;
        _multiSessions.forEach((s) => {
          if (s.contourUIDs.length > 0) {
            allOrphaned = false;
          }
        });
        if (allOrphaned) {
          _multiSessions.forEach((s) => {
            for (const uid of s.clickMarkers) {
              try {
                cornerstoneTools.annotation.state.removeAnnotation(uid);
              } catch {
                /* noop */
              }
            }
          });
          _multiSessions.clear();
        }
      }

      if (_multiSessions.size === 0) {
        regionGrowImageIds.clear();
        _setNnClickMarkers?.(() => []);
      }
    } else {
      session.interactions = prev.interactions;
      session.sessionId = prev.sessionId;
      session.lastResponse = prev.response;
      // Re-materialize from the snapshot response (not the dangling prev
      // contourUIDs a refine already removed) so the prior step actually renders.
      restoreContourFromSnapshot(session, prev, restoreSegIdx);
    }

    // Re-render viewports — if orphaned companions were cleared, render all siblings
    const siblings = _getSiblingViewports?.();
    if (siblings) {
      for (const p of PLANE_KEYS) {
        const el = siblings[p]?.viewport?.element;
        if (el) {
          try {
            cornerstoneTools.utilities.triggerAnnotationRender(el);
          } catch {
            /* noop */
          }
        }
      }
    } else if (session.viewport?.element) {
      try {
        cornerstoneTools.utilities.triggerAnnotationRender(session.viewport.element);
      } catch {
        /* noop */
      }
    }
    // Pop the last click marker for this viewport
    const vpId = session.viewport?.id;
    if (vpId) {
      _setNnClickMarkers?.((prev) => {
        const idx = prev.findLastIndex((m) => m.viewportId === vpId);
        return idx >= 0 ? [...prev.slice(0, idx), ...prev.slice(idx + 1)] : prev;
      });
    }

    updateMultiSeries3DViewer();
    syncSessionActive(); // #219: sync atom when per-plane undo removes a session
    return true;
  }

  // Fallback: undo all sessions (atomic)
  let undid = false;
  _multiSessions.forEach((session) => {
    if (session.undoStack.length > 0) {
      const prev = session.undoStack.pop()!;
      const restoreSegIdx = readSessionSegmentIndex(session);
      for (const uid of prev.geometry ? [] : session.contourUIDs) {
        try {
          cornerstoneTools.annotation.state.removeAnnotation(uid);
        } catch {
          /* noop */
        }
      }
      if (prev.interactions.length === 0 && !prev.sessionId) {
        if (prev.geometry) restoreGeometryCheckpoint(session.viewport, prev.geometry);
        _multiSessions.delete(session.plane);
      } else {
        session.interactions = prev.interactions;
        session.sessionId = prev.sessionId;
        session.lastResponse = prev.response;
        restoreContourFromSnapshot(session, prev, restoreSegIdx);
      }
      undid = true;
    }
  });
  if (_multiSessions.size === 0) {
    regionGrowImageIds.clear();
    _setNnClickMarkers?.(() => []); // Clear all markers (#203)
  }

  const siblings = _getSiblingViewports?.();
  if (siblings) {
    for (const p of PLANE_KEYS) {
      const el = siblings[p]?.viewport?.element;
      if (el)
        try {
          cornerstoneTools.utilities.triggerAnnotationRender(el);
        } catch {
          /* noop */
        }
    }
  }
  updateMultiSeries3DViewer();
  syncSessionActive();
  return undid;
}

/**
 * Reject the entire nnInteractive segmentation. Removes all contours
 * from all sessions and clears state.
 * Returns true if there was something to reject.
 */
export function rejectNnInteractive(
  cornerstoneTools: any,
  regionGrowImageIds: Set<string>,
): boolean {
  _cancelPendingNnOperation?.();
  if (!hasNnInteractiveSession()) return false;

  _multiSessions.forEach((session) => {
    for (const uid of session.contourUIDs) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(uid);
      } catch {
        /* noop */
      }
    }
    for (const uid of session.clickMarkers) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(uid);
      } catch {
        /* noop */
      }
    }
  });

  _multiSessions.clear();
  _nnRequestInFlight = false;
  // Reject after one oversized strike must not carry over: the next click
  // could otherwise fall back on its first oversized result.
  _consecutiveOversizedCount = 0;
  regionGrowImageIds.clear();
  _setNnClickMarkers?.(() => []); // Clear all markers (#203)
  // Loading spinner can be in-flight when reject fires (mid-request reject via
  // Escape or tool switch). Clear it so the overlay doesn't hang. (#396)
  _setNnLoading?.({ isLoading: false, viewportId: null });

  const siblings = _getSiblingViewports?.();
  if (siblings) {
    for (const p of PLANE_KEYS) {
      const el = siblings[p]?.viewport?.element;
      if (el)
        try {
          cornerstoneTools.utilities.triggerAnnotationRender(el);
        } catch {
          /* noop */
        }
    }
  }

  updateMultiSeries3DViewer();
  syncSessionActive();
  return true;
}
